/**
 * TypeScript mirror of docs/ops-protocol.md.
 *
 * The Swift core emits a stream of ops that the renderer applies to the DOM.
 * Element ids are integers assigned by Swift; id 0 is the root container (the
 * device screen) and is never created or removed.
 */

export type ElementId = number;

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

/** Explicit sRGB color; every component is in 0...1. */
export interface RGBAColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export type SemanticColorName =
  | 'primary'
  /** `.foreground` (ForegroundStyle): the color where it is drawn (CSS `currentColor`). */
  | 'foreground'
  | 'secondary'
  | 'accent'
  | 'systemBackground'
  | 'secondarySystemBackground'
  /** UIKit's semantic colors, by their iOS names (`Color(uiColor:)`). */
  | 'tertiarySystemBackground'
  | 'systemGroupedBackground'
  | 'secondarySystemGroupedBackground'
  | 'tertiarySystemGroupedBackground'
  | 'label'
  | 'secondaryLabel'
  | 'tertiaryLabel'
  | 'quaternaryLabel'
  | 'placeholderText'
  | 'separator'
  | 'opaqueSeparator'
  | 'systemFill'
  | 'secondarySystemFill'
  | 'tertiarySystemFill'
  | 'quaternarySystemFill'
  | 'systemGray2'
  | 'systemGray3'
  | 'systemGray4'
  | 'systemGray5'
  | 'systemGray6'
  /** The system palette (`Color.red`, `UIColor.systemRed`…): `--sb-color-<name>`. */
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'mint'
  | 'teal'
  | 'cyan'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'pink'
  | 'brown'
  | 'gray'
  | 'clear'
  /** `TintShapeStyle` (`.tint` as a style): the nearest `.tint(_:)`, else the accent color. */
  | 'tint';

/**
 * Semantic color the renderer resolves for the current color scheme. `a`
 * (0...1) is an optional opacity multiplier applied on top of the token.
 */
export interface NamedColor {
  name: SemanticColorName;
  a?: number;
}

/**
 * Phase 5: a color from the app's asset catalog (a `.colorset`, possibly
 * namespaced like `dough/plain-bg`). The renderer resolves it for the current
 * color scheme through the catalog manifest (src/assets.ts); `opacity`
 * (0...1) multiplies the catalog alpha. Unknown names render magenta.
 */
export interface AssetColor {
  named: string;
  opacity: number;
}

export type Color = RGBAColor | NamedColor | AssetColor;

// Phase 6 (docs/phase6/styles.md): ShapeStyle, everything a view can be
// painted with. A `Color` is one; the others resolve to CSS that depends on
// the role (text or paint), see src/styles.ts.

/** A unit point in the view's bounds (0...1 on each axis). */
export interface UnitPoint {
  x: number;
  y: number;
}

/**
 * Every non-color ShapeStyle may carry an `opacity` multiplier (0...1) at
 * its top level (`.quaternary.opacity(0.5)`, `.thinMaterial.opacity(0.8)`),
 * applied like a semantic color's `a`.
 */
export interface StyleOpacity {
  opacity?: number;
}

/**
 * `.primary` ... `.quaternary`. As a foreground (text) the label colors; as a
 * background, fill or stroke the iOS fill colors of the same level.
 */
export interface HierarchicalStyle extends StyleOpacity {
  hierarchical: 1 | 2 | 3 | 4;
}

export interface GradientStop {
  color: Color;
  /** 0...1 along the gradient. */
  location: number;
}

export interface LinearGradient {
  stops: GradientStop[];
  start: UnitPoint;
  end: UnitPoint;
}
export interface LinearGradientStyle extends StyleOpacity {
  linearGradient: LinearGradient;
}

export interface RadialGradient {
  stops: GradientStop[];
  center: UnitPoint;
  /** Points. */
  startRadius: number;
  endRadius: number;
}
export interface RadialGradientStyle extends StyleOpacity {
  radialGradient: RadialGradient;
}

export interface AngularGradient {
  stops: GradientStop[];
  center: UnitPoint;
  /** Degrees, clockwise from the positive x axis (3 o'clock); the stops span start...end. */
  startAngle: number;
  endAngle: number;
}
export interface AngularGradientStyle extends StyleOpacity {
  angularGradient: AngularGradient;
}

/** `Color.gradient`: a subtle vertical gradient derived from one color. */
export interface ColorGradientStyle extends StyleOpacity {
  colorGradient: Color;
}

/** `glass` / `glassClear`: iOS 26 Liquid Glass (`.glassEffect`), the bar buttons' fill with the glass rim. */
export type MaterialName = 'ultraThin' | 'thin' | 'regular' | 'thick' | 'ultraThick' | 'glass' | 'glassClear';
/** A translucent material: the system background at some opacity over a blurred backdrop. */
export interface MaterialStyle extends StyleOpacity {
  material: MaterialName;
}

export type ShapeStyle =
  | Color
  | HierarchicalStyle
  | LinearGradientStyle
  | RadialGradientStyle
  | AngularGradientStyle
  | ColorGradientStyle
  | MaterialStyle;

// ---------------------------------------------------------------------------
// Style
// ---------------------------------------------------------------------------

export interface EdgeInsets {
  top?: number;
  leading?: number;
  bottom?: number;
  trailing?: number;
}

export type FontWeight =
  | 'ultraLight'
  | 'thin'
  | 'light'
  | 'regular'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'heavy'
  | 'black';
export type FontDesign = 'default' | 'serif' | 'rounded' | 'monospaced';

/** iOS text styles; resolved by the renderer for the current Dynamic Type size. */
export type TextStyle =
  | 'largeTitle'
  | 'title'
  | 'title2'
  | 'title3'
  | 'headline'
  | 'subheadline'
  | 'body'
  | 'callout'
  | 'footnote'
  | 'caption'
  | 'caption2';

/**
 * Every field is optional. `size` and `textStyle` are mutually exclusive: a
 * `textStyle` scales with Dynamic Type, a fixed `size` does not unless
 * `relativeTo` names the text style it should scale like. `headline` implies
 * `semibold` unless `weight` is given.
 */
export interface Font {
  textStyle?: TextStyle;
  size?: number;
  relativeTo?: TextStyle;
  weight?: FontWeight;
  design?: FontDesign;
  /** `.monospacedDigit()`: digits of one width (`font-variant-numeric: tabular-nums`). */
  monospacedDigit?: boolean;
}

export type FrameAlignment =
  | 'center'
  | 'leading'
  | 'trailing'
  | 'top'
  | 'bottom'
  | 'topLeading'
  | 'topTrailing'
  | 'bottomLeading'
  | 'bottomTrailing';

/** A frame dimension: points, or `"infinity"` to fill the available space. */
export type Dimension = number | 'infinity';

export interface Frame {
  width?: number | null;
  height?: number | null;
  minWidth?: Dimension | null;
  maxWidth?: Dimension | null;
  minHeight?: Dimension | null;
  maxHeight?: Dimension | null;
  /** Positions the single child inside the frame; defaults to `center`. */
  alignment?: FrameAlignment;
}

// Phase 3 animation (docs/ops-protocol.md, "Phase 3 additions").

export type AnimationType = 'default' | 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'spring';

/**
 * `duration` defaults to 0.35s. For `spring` it is the perceptual duration
 * and `bounce` (0 = critically damped, 0.3 = bouncy) shapes the curve.
 */
export interface Animation {
  type: AnimationType;
  duration?: number;
  bounce?: number;
  delay?: number;
}

export type TransitionKind = 'opacity' | 'scale' | 'slide' | 'move' | 'offset' | 'identity' | 'asymmetric';
export type Edge = 'top' | 'leading' | 'bottom' | 'trailing';

export interface TransitionPart {
  kind: TransitionKind;
  /** `move` only. */
  edge?: Edge;
  /** `scale` only; defaults to 0.5. */
  scale?: number;
  /** `offset` only: the translation (points) the view enters from / exits to. */
  x?: number;
  y?: number;
  /**
   * Phase 6, `asymmetric` only (`.asymmetric(insertion:removal:)`): the
   * transition played when the view is inserted, and the one when removed.
   */
  insertion?: Transition;
  removal?: Transition;
}

/** One transition, or several combined. */
export type Transition = TransitionPart | TransitionPart[];

export type TextAlignment = 'leading' | 'center' | 'trailing';

export interface FixedSize {
  horizontal: boolean;
  vertical: boolean;
}

// Phase 5 (docs/ops-protocol.md, "Phase 5 additions").

export type ContentMode = 'fit' | 'fill';

/**
 * `.aspectRatio(_:contentMode:)`. `ratio: null` keeps the child's own ideal
 * aspect ratio (`.scaledToFit()` is `{ ratio: null, contentMode: "fit" }`,
 * `.scaledToFill()` the `fill` form).
 */
export interface AspectRatio {
  ratio: number | null;
  contentMode: ContentMode;
}

/** `.shadow(color:radius:x:y:)`; rendered as a `drop-shadow` filter. */
export interface Shadow {
  color: Color;
  radius: number;
  x: number;
  y: number;
}

/** Every field is optional. */
export interface Style {
  padding?: EdgeInsets;
  font?: Font;
  /** Phase 6: any ShapeStyle (text role). Gradients paint the text itself. */
  foreground?: ShapeStyle;
  /**
   * `.foregroundStyle(primary, secondary[, tertiary])`: the styles after the
   * first, the `.secondary` and `.tertiary` levels below (`--sb-hierarchy-2`, `-3`).
   */
  foregroundLevels?: ShapeStyle[];
  /** Phase 6: any ShapeStyle (paint role). */
  background?: ShapeStyle;
  frame?: Frame;
  cornerRadius?: number;
  /**
   * `.background(_:)` in a container shape (`.containerShape`): the
   * background's corner radius. Unlike `cornerRadius` it does not clip.
   */
  backgroundCornerRadius?: number;
  opacity?: number;
  /** Dims the subtree (`opacity: 0.4`) and blocks input (`pointer-events: none`). */
  disabled?: boolean;
  // Phase 3
  /** Stack space distribution priority (default 0). */
  layoutPriority?: number;
  /** Maximum lines of text in the subtree; `null` lifts an inherited limit. */
  lineLimit?: number | null;
  multilineTextAlignment?: TextAlignment;
  /** The child is proposed `nil` on the fixed axes and takes its ideal size. */
  fixedSize?: FixedSize;
  /**
   * `.animation(_:value:)`: whenever `animationToken` changes in an `update`,
   * the layout and style changes of this subtree in that commit animate with
   * `animation`.
   */
  animation?: Animation;
  animationToken?: number;
  /** This box is a `Label`'s icon: a list row tints it (its title keeps the row's color). */
  labelIcon?: boolean;
  /** `.textSelection(.enabled)`: the text below can be selected (iOS shows none selectable otherwise). */
  textSelection?: boolean;
  /** How this subtree appears and disappears inside an animated commit. */
  transition?: Transition;
  // Phase 5
  /** Sizes the child to an aspect ratio inside the proposal (see `AspectRatio`). */
  aspectRatio?: AspectRatio;
  // Phase 6 visual effects (docs/phase6/styles.md): CSS only, no layout effect.
  /** `.offset(x:y:)`: translation in points. */
  offset?: { x: number; y: number };
  /** `.rotationEffect`: degrees, clockwise positive. */
  rotation?: number;
  /** Unit point the rotation turns around (default 0.5/0.5). */
  rotationAnchor?: UnitPoint;
  /** `.scaleEffect`: per-axis scale factors. */
  scale?: { x: number; y: number };
  /** Unit point the scale grows from (default 0.5/0.5). */
  scaleAnchor?: UnitPoint;
  shadow?: Shadow;
  /**
   * `.compositingGroup()`: the box is drawn as one layer before the effects around it apply, so a `shadow` (here or
   * on a box around it) is cast by the box as a whole rather than by each view it draws.
   */
  compositingGroup?: boolean;
  /** `.clipped()`: `overflow: hidden` on the box. */
  clipped?: boolean;

  // Phase 6 (docs/phase6/layout-containers.md)
  /** `.gridCellColumns(_:)`: how many `grid` columns the cell spans (default 1). */
  gridCellColumns?: number;
  /** `.gridCellAnchor(_:)`: unit point the cell is aligned by inside its cell box. */
  gridCellAnchor?: UnitPoint;
  /** `.tint(_:)`: sets `--sb-tint` on the box so gauges, switches and prominent buttons below pick it up. */
  tint?: Color;
  /**
   * `.position(x:y:)`: the box takes the whole proposal (the child's ideal
   * size on nil axes) and centers the child, measured with a nil proposal,
   * at (x, y) inside it.
   */
  position?: Point;

  // Phase 6 (docs/phase6/chrome.md)
  /**
   * `.badge(_:)`: when the styled element is a list row, this text is drawn
   * trailing in the row in the secondary color (17pt) and its width is
   * reserved, so the row content is proposed less width. `null` clears it.
   */
  badge?: string | null;
  /**
   * `.listRowInsets(_:)`: replaces the row's default insets (16 horizontal,
   * 11 vertical) for the list row this styled element is.
   */
  listRowInsets?: EdgeInsets;
  /**
   * `.listRowBackground(_:)` with a `Color` (or ShapeStyle): the list row this
   * styled element is (or is in) has its cell painted with it instead of the
   * card's; `clear` shows the list's background.
   */
  listRowBackground?: ShapeStyle;
  /**
   * `.imageScale(_:)`: symbol images in the subtree (`image` with
   * `systemName`, and catalog `symbol` images) measure 0.8 / 1 / 1.25 of the
   * font line height.
   */
  imageScale?: ImageScale;
}


export interface Point {
  x: number;
  y: number;
}

export type ImageScale = 'small' | 'medium' | 'large';

// ---------------------------------------------------------------------------
// Element kinds and props
// ---------------------------------------------------------------------------

export type VStackAlignment = 'leading' | 'center' | 'trailing';
export type HStackAlignment = 'top' | 'center' | 'bottom';

export interface TextProps {
  text: string;
  /**
   * Rich text (`Text + Text`, `Text(AttributedString)`, an interpolated
   * image, a Text-only modifier such as `.italic()`): the text as runs, each
   * styled on its own. `text` is then the plain content.
   */
  runs?: TextRun[];
}
/**
 * One run of a rich text: its `text`, or an inline `image` (an `image`
 * element's props). Unset fields take the text's own style.
 */
export interface TextRun {
  text?: string;
  image?: ImageProps;
  /** A Font JSON object applied over the text's font (as `styled` `font`). */
  font?: Record<string, unknown>;
  italic?: boolean;
  /** A ShapeStyle JSON object (as `styled` `foreground`). */
  foreground?: unknown;
  /** A Color JSON object behind the run's glyphs (`AttributedString`'s `backgroundColor`). */
  background?: unknown;
  /** A line through or under the run; `color` defaults to the run's own. */
  strikethrough?: { color?: unknown };
  underline?: { color?: unknown };
  /** Extra space after each character, in points. */
  kerning?: number;
  /** Points to raise the run by (negative lowers it). */
  baselineOffset?: number;
  /** A tap on the run sends `link` with this URL. */
  link?: string;
}
export interface VStackProps {
  spacing: number | null;
  alignment: VStackAlignment;
  /** Phase 12 `.scrollTargetLayout()`: the children are the enclosing scroll view's snap points. */
  scrollTarget?: boolean;
}
export interface HStackProps {
  spacing: number | null;
  alignment: HStackAlignment;
  scrollTarget?: boolean;
}
export interface SpacerProps {
  minLength: number | null;
}
/** `borderless` is `.borderless`: text-only in the tint, as `automatic`, and SwiftUI's own natively. */
export type ButtonStyle = 'automatic' | 'plain' | 'borderless' | 'bordered' | 'borderedProminent' | 'glass';
/** Phase 8: `cancel` is the role an `alert` draws apart (bold, or in its own card). */
export type ButtonRole = 'destructive' | 'cancel' | null;
/**
 * The label is the button's child. Phase 1 modules send `{}`; both fields are
 * therefore optional and default to `automatic` / no role.
 */
export interface ButtonProps {
  style?: ButtonStyle;
  role?: ButtonRole;
  /** An `EditButton`: the edit mode it shows ("Done" / "Edit") and flips. A native renderer draws the platform's edit button from it. */
  editMode?: 'active' | 'inactive';
}
export interface StyledProps {
  style: Style;
}

// Phase 2 kinds (docs/ops-protocol.md, "Phase 2 additions").

/** Same alignment names as `Frame.alignment`; later children draw on top. */
export interface ZStackProps {
  alignment: FrameAlignment;
  /**
   * Report the element's laid-out size with `geometry` events, as a
   * `geometry` element does (an anchor preference's wrapper; see
   * docs/ops-protocol.md, "Preferences").
   */
  reportsSize?: boolean;
}
/** `systemName` is an SF Symbol name; the renderer picks the closest glyph. */
export interface SymbolImageProps {
  systemName: string;
  /**
   * `.symbolRenderingMode(.palette)`: an enclosed filled symbol
   * (`moon.circle.fill`) draws its enclosure in the secondary level
   * (`--sb-hierarchy-2`) and its glyph in the primary, instead of knocking
   * the glyph out.
   */
  renderingMode?: 'palette';
}
/**
 * Phase 5: an image from the app's asset catalog. `name` is the catalog
 * name, possibly namespaced (`dough/plain-full`). A non-resizable image is
 * always its intrinsic point size; a `.resizable()` one fills the proposal
 * (the intrinsic size on unconstrained axes). The renderer looks the name up
 * in the catalog manifest (src/assets.ts).
 */
export interface AssetImageProps {
  name: string;
  resizable: boolean;
}
/**
 * Phase 13: a remote image (`AsyncImage`). The page loaded it already (an
 * `image` request op) and told the app its pixel size; `width` / `height`
 * are that size in points, the intrinsic size the layout engine uses unless
 * `resizable`.
 */
export interface UrlImageProps {
  url: string;
  resizable: boolean;
  width: number;
  height: number;
  /**
   * React's opt-in (`AsyncImage webInteractions`): the `<img>` keeps the
   * browser's own image interactions (a long press's Save / Share / Copy on
   * iOS, dragging it out, the right-click menu) instead of being inert as a
   * SwiftUI image is. Swift apps never send it.
   */
  webInteractions?: boolean;
}
/**
 * A `UIImage` / `CGImage` bitmap (`Image(uiImage:)`): a recipe the page draws
 * (src/bitmaps.ts). `width` / `height` are the image's size in points, the
 * intrinsic size unless `resizable`. See docs/ops-protocol.md, "Bitmaps and
 * Core Image".
 */
export interface BitmapImageProps {
  bitmap: BitmapRecipe;
  resizable: boolean;
  width: number;
  height: number;
  /** `.interpolation(.none)`: scaled without smoothing. */
  interpolation?: 'none';
}
export type ImageProps = SymbolImageProps | AssetImageProps | UrlImageProps | BitmapImageProps;

/**
 * How to draw a bitmap: the `imagedata` source named by `source` (null: an
 * empty image) with `steps` applied in order, in Core Image's coordinate
 * space (units of source pixels, origin at the bottom left). The result is
 * `width` × `height` pixels.
 */
export interface BitmapRecipe {
  source: string | null;
  /** A frame of an animated source (a GIF), composed as the frames before it leave the canvas; absent: the file as it is. */
  frame?: number;
  steps: BitmapStep[];
  width: number;
  height: number;
}

/** A point in Core Image's space. */
export type BitmapPoint = [number, number];

/** One step of a recipe; the formulas are in docs/ops-protocol.md. */
export type BitmapStep =
  | { op: 'crop' | 'render'; x: number; y: number; width: number; height: number }
  | { op: 'clamp' }
  | { op: 'sepiaTone'; intensity: number }
  | { op: 'edges'; intensity: number }
  | { op: 'crystallize'; radius: number; center: BitmapPoint }
  | { op: 'pixellate'; scale: number; center: BitmapPoint }
  | { op: 'gaussianBlur'; radius: number }
  | { op: 'unsharpMask'; radius: number; intensity: number }
  | { op: 'vignette'; intensity: number; radius: number; center: BitmapPoint; size: number }
  /** Core Graphics drawing an image in a rectangle: every point p goes to (p.x × sx + tx, p.y × sy + ty). */
  | { op: 'place'; sx: number; sy: number; tx: number; ty: number }
  /** A clip to a rounded rectangle (an ellipse when the radii are half its sides). */
  | { op: 'roundedClip'; x: number; y: number; width: number; height: number; rx: number; ry: number }
  /** `UIImage.withTintColor`: every pixel the color (straight RGBA, 0…1), at the pixel's alpha times the color's. */
  | { op: 'tint'; r: number; g: number; b: number; a: number };
export type DividerProps = Record<string, never>;
export type ScrollAxes = 'vertical' | 'horizontal' | 'both';
export interface ScrollViewProps {
  axes: ScrollAxes;
}
/**
 * Phase 4: `insetGrouped` (the default when omitted, as Phase 2 modules send
 * `{}`), `grouped` (full-width sections with hairlines; `Form`) or `plain`.
 */
/** `sidebar`: a `NavigationSplitView`'s sidebar list (and `.listStyle(.sidebar)`): rows on the background, collapsible sections. */
export type ListStyle = 'plain' | 'insetGrouped' | 'grouped' | 'sidebar';
export interface ListProps {
  style?: ListStyle;
  /** A `Form`: on a Mac the page lays its rows out in the columns style (labels in a column before their controls). */
  form?: boolean;
  /** `.formStyle(_:)` other than `automatic`: on a Mac `grouped` lays the form out in grouped sections. */
  formStyle?: 'grouped' | 'columns';
  /**
   * `.environment(\.defaultMinListRowHeight, _)`: the rows are at least
   * this tall (points). Absent, the platform's default: 44 on iOS, 24 on a
   * Mac. A Mac app spaces a content list's rows like a sidebar's, 32.
   */
  minRowHeight?: number;
}
export interface SectionProps {
  header: string | null;
  footer: string | null;
  /** `Section(isExpanded:)`: the section collapses in a sidebar-style list (as on iOS); a `tap` on it toggles. The rows come whatever the state. */
  collapsible?: boolean;
  expanded?: boolean;
}
/** `DisclosureGroup`: open or not; its children are the label (first, a `zstack`) and the content, laid out when expanded; a `tap` toggles. */
export interface DisclosureGroupProps {
  expanded: boolean;
}
export type TextFieldStyle = 'plain' | 'roundedBorder';
export interface TextFieldProps {
  text: string;
  placeholder: string;
  style: TextFieldStyle;
  secure: boolean;
  /**
   * Phase 12 `.focused(...)`: whether the field should have focus; present
   * only on bound fields, which report `focus` events. The value the app's
   * pass for a `submit` leaves (true again, or another field's turned true)
   * wins over the platform's dismissal of the keyboard, and a field turning
   * true is scrolled into view ("Focus across a submit").
   */
  focused?: boolean;
  /** Phase 12 `.onSubmit`: the Enter key sends a `submit` event. */
  submit?: boolean;
  /**
   * `onDeleteBackward`: Backspace with the caret at the start of the text (an
   * empty text included) sends `deletebackward` instead of deleting nothing
   * (docs/ops-protocol.md, "The delete key at the start of a field").
   */
  deleteBackward?: boolean;
  /** Phase 12 `.submitLabel`: the return key's label (`enterkeyhint`). */
  submitLabel?: string;
  /** `.keyboardType(_:)`: a `UIKeyboardType` case name (`emailAddress`, `URL`, `numberPad`…), the on-screen keyboard (`inputmode`). */
  keyboard?: string;
  /** `.textContentType(_:)`: a `UITextContentType` name (`emailAddress`, `oneTimeCode`…), what autofill offers (`autocomplete`). */
  contentType?: string;
  /** `.textInputAutocapitalization(_:)` (`autocapitalize`). */
  autocapitalization?: 'never' | 'words' | 'sentences' | 'characters';
  /** `.autocorrectionDisabled()` (`autocorrect="off"`). */
  autocorrectionDisabled?: boolean;
}
/** `.toggleStyle`: the switch (`automatic`, `switch`) or `button`, a button tinted while on. */
export type ToggleStyle = 'automatic' | 'switch' | 'button';
export interface ToggleProps {
  isOn: boolean;
  /** `.toggleStyle(_:)`; absent is `automatic` (the switch). */
  style?: ToggleStyle;
}
export type NavStackProps = Record<string, never>;
export type NavDisplayMode = 'automatic' | 'large' | 'inline';
export interface NavScreenProps {
  title: string;
  /** `.navigationSubtitle`: a second line under the title in a Mac's unified title bar, after it in a plain one. */
  subtitle?: string;
  displayMode: NavDisplayMode;
  /** 0 for the root screen; the renderer draws a back button when > 0. */
  depth: number;
  /**
   * `.toolbar(.hidden, for: .navigationBar)`: no navigation bar (nor large
   * title, back button or bar items); the content starts under the status
   * bar. A change fades the bar in an animated commit.
   */
  navigationBarHidden?: boolean;
  /** `.toolbar(.hidden, for: .bottomBar)`: the `bottom` toolbars draw nothing. */
  bottomBarHidden?: boolean;
  /** `.statusBarHidden()`: natively the status bar hides with the screen; the page's frame mode hides the status bar it draws (the safe area stays). */
  statusBarHidden?: boolean;
}
/** Phase 7 adds `selected` (see the block at the end of this file). */
export interface NavLinkProps {
  selected?: boolean;
  /** `.buttonStyle(.plain)` around the link (absent otherwise): its label in the foreground it is in, not the tint. */
  style?: 'plain';
}

// Phase 3 kinds (docs/ops-protocol.md, "Phase 3 additions").

/** A box filled with `color`; fills its proposal. */
export interface ColorProps {
  color: Color;
}
/** Phase 6 adds `containerRelative` (`ContainerRelativeShape`): a rounded rectangle with `cornerRadius`. */
export type ShapeKind = 'rectangle' | 'roundedRectangle' | 'circle' | 'capsule' | 'ellipse' | 'containerRelative'
  /** A custom shape drawn from its path (`ShapeProps.path`). */
  | 'path';
export interface Stroke {
  /** Phase 6: any ShapeStyle; a gradient stroke falls back to its first stop. */
  color: ShapeStyle;
  lineWidth: number;
  /**
   * Phase 6: `.strokeBorder` draws the stroke inside the shape's bounds. The
   * renderer draws every stroke inside the box; `inset` is recorded as
   * `data-sb-stroke`.
   */
  inset?: boolean;
  /** A custom shape's `StrokeStyle` (a built-in shape's border has none of these but `dash`). */
  lineCap?: 'butt' | 'round' | 'square';
  lineJoin?: 'miter' | 'round' | 'bevel';
  miterLimit?: number;
  /** A built-in shape draws any dash pattern as CSS dashes. */
  dash?: number[];
  dashPhase?: number;
}
/** A path for any size: `ShapeProps.affinePath`. */
export interface AffinePath {
  path: string;
  width: number[];
  height: number[];
}

/** `fill: null` means the current foreground color; fills its proposal. */
export interface ShapeProps {
  shape: ShapeKind;
  cornerRadius: number;
  /** Phase 6: any ShapeStyle. */
  fill: ShapeStyle | null;
  stroke: Stroke | null;
  /**
   * `.trim(from:to:)`: the part of the outline between the fractions, drawn
   * as a conic mask around the center from 3 o'clock, clockwise (exact for a
   * circle). Absent when the shape is not trimmed.
   */
  trim?: { from: number; to: number };
  /**
   * A custom shape (`shape: "path"`, src/pathShape.ts): SVG path data for a
   * rectangle of `size` at the origin, the size the element reported
   * (`reportsSize`). Empty until the first report.
   */
  path?: string;
  size?: { width: number; height: number };
  /**
   * A custom shape's path for any size (`path` and `size` unused): SVG path
   * data whose numbers are their values for an empty rectangle, the `i`th
   * growing `width[i]` a point of the element's width and `height[i]` a
   * point of its height. Drawn at the element's laid-out size, so from its
   * first layout, with no size reported.
   */
  affinePath?: AffinePath;
  /** `FillStyle(eoFill: true)`: overlapping parts of the path cancel out. */
  fillRule?: 'evenodd';
  /** The element reports its laid-out size (a custom shape draws its path for it). */
  reportsSize?: boolean;
  /**
   * An animated change of the shape's `animatableData`: the path at each
   * fraction of the change (`paths[index[i]]` at `progress[i]`), played in
   * the commit's animation.
   */
  frames?: { progress: number[]; index: number[]; paths: string[] };
}
/** Phase 8: `full` is `.fullScreenCover` (the card fills the screen). */
/** `height:<points>` / `fraction:<f>`: `.presentationDetents([.height(300)])`, `.fraction(0.4)`. */
export type Detent = 'medium' | 'large' | 'full' | `height:${number}` | `fraction:${number}`;
/**
 * Always a root-level element (child of id 0) placed after the app's tree. A
 * `tap` with the sheet's own id is a dismiss request.
 */
export interface SheetProps {
  detents: Detent[];
}

// Phase 4 kinds (docs/ops-protocol.md, "Phase 4 additions").

/** Fills the screen; a 49pt tab bar plus the bottom safe area at the bottom. */
export interface TabViewProps {
  /** Index of the selected `tab` child (authoritative; a tap sends `select`). */
  selected: number;
}
/** One tab of a `tabview`: its content is the child; the bar item shows `title` and `systemImage`. */
export interface TabProps {
  title: string;
  systemImage: string | null;
  /** iOS 18 `Tab(…).badge(…)`: a red badge on the bar item (absent when none). */
  badge?: string;
  /** iOS 18 `Tab(role: .search)` (absent otherwise); Swift already put it last. */
  role?: 'search';
  /** A catalog image (a custom symbol) for the bar item when there is no `systemImage` (absent otherwise). */
  image?: string;
}
export type PickerStyle = 'segmented' | 'menu' | 'inline';
/**
 * `segmented`: an iOS segmented control (32pt). `menu`: a row with `label`
 * leading and the selected option plus a chevron trailing that opens a menu.
 * `inline` (Phase 6): one 44pt row per option with a checkmark on the
 * selected one, preceded by a dim `label` row when the label is non-empty;
 * inside a `menu` element the options become a section of the popover.
 * Choosing sends `select` with the index.
 */
export interface PickerProps {
  style: PickerStyle;
  label: string;
  options: string[];
  selected: number;
}
/**
 * `GeometryReader`: fills its proposal and reports its size through a
 * `geometry` event whenever it changes. Exactly one child.
 */
export interface GeometryProps {
  /**
   * Report the reader's frame on screen too (`x`, `y` and `safeArea` in its
   * `geometry` events, kept current as its ancestors scroll), for
   * `GeometryProxy.frame(in: .global)` and `safeAreaInsets`. Absent: the size
   * only, as before.
   */
  reportsFrame?: boolean;
  /**
   * The Swift view the size is reported to. Swift keeps the element when
   * another view takes the reader's place, and changes this: the next layout
   * pass then reports the size again, unchanged or not, since the new view
   * has not heard it. Also on every other element that reports its size
   * (`reportsSize`).
   */
  reader?: number;
}

// Phase 6 kinds (docs/phase6/layout-containers.md).

/** A proposal axis as Swift sees it: points, `"infinity"` or `null` (nil). */
export type ProposalDimension = number | 'infinity' | null;
export interface LayoutProposal {
  width: ProposalDimension;
  height: ProposalDimension;
}
export interface LayoutSize {
  width: number;
  height: number;
}
export interface LayoutFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}
/**
 * A custom SwiftUI `Layout` evaluated on the Swift side with host-measured
 * subviews. The children are the subviews, in order. `size` and `frames`
 * (relative to the element's origin, one per child) are Swift's answer for
 * `proposal`, or `null` before the first answer; the renderer measures the
 * children and reports them through a `layout` event.
 */
export interface LayoutProps {
  proposal: LayoutProposal | null;
  size: LayoutSize | null;
  frames: LayoutFrame[] | null;
  /**
   * The layout's own `sizeThatFits` for a zero, unspecified and infinite
   * proposal (`"infinity"` for an unbounded dimension), sent with every
   * answer. A parent measuring the layout with a proposal other than the
   * answered one reads its flexibility from these instead of guessing.
   */
  sizes?: LayoutSizes | null;
}
export interface LayoutSizes {
  min: LayoutFlexSize;
  ideal: LayoutFlexSize;
  max: LayoutFlexSize;
}
/** A size whose dimensions may be unbounded. */
export interface LayoutFlexSize {
  width: number | 'infinity';
  height: number | 'infinity';
}
/** `Grid`: `horizontalSpacing`/`verticalSpacing` default to 8. Children are `gridrow`s or single full-span cells. */
export interface GridProps {
  horizontalSpacing: number | null;
  verticalSpacing: number | null;
  alignment: FrameAlignment;
}
export type GridRowAlignment = 'top' | 'center' | 'bottom' | 'firstTextBaseline' | 'lastTextBaseline';
/** `GridRow`: the children are the cells. `alignment: null` uses the grid's. */
export interface GridRowProps {
  alignment: GridRowAlignment | null;
}
export type GridItemSize =
  | { kind: 'fixed'; value: number }
  | { kind: 'flexible'; minimum: number; maximum: Dimension }
  | { kind: 'adaptive'; minimum: number; maximum: Dimension };
/** `GridItem`: `spacing` (to the next column) defaults to 8, `alignment` to the grid's. */
export interface GridItem {
  size: GridItemSize;
  spacing: number | null;
  alignment: FrameAlignment | null;
}
/** `LazyVGrid`: `spacing` between rows defaults to 8. Children are the cells (flattened). Nothing is lazy. */
export interface LazyVGridProps {
  columns: GridItem[];
  spacing: number | null;
  alignment: FrameAlignment;
}
/**
 * `LazyHGrid`: `LazyVGrid` turned on its side. The `rows` share the height
 * the grid is proposed; the cells fill them column by column, `spacing`
 * (default 8) apart. Children are the cells (flattened). Nothing is lazy.
 */
export interface LazyHGridProps {
  rows: GridItem[];
  spacing: number | null;
  alignment: FrameAlignment;
}
export type GaugeStyle =
  | 'automatic'
  | 'linearCapacity'
  | 'accessoryLinear'
  | 'accessoryCircular'
  | 'accessoryCircularCapacity'
  /** A determinate `ProgressView`: the label above the bar, the current value label under it. */
  | 'progress';
/**
 * `Gauge`: `value` is normalized to 0...1. Children in order: the label (when
 * `hasLabel`) and the current value label (when `hasCurrentValueLabel`).
 * Circular styles draw a ring with one of the labels in the middle.
 */
export interface GaugeProps {
  value: number;
  style: GaugeStyle;
  hasLabel: boolean;
  hasCurrentValueLabel: boolean;
}
export type DecorationRole = 'overlay' | 'background';
/**
 * `.overlay { }` / `.background { }` with a view: children are
 * `[content, decoration]`. The decoration is proposed the content's size,
 * aligned inside it and never affects the size.
 */
export interface DecoratedProps {
  role: DecorationRole;
  alignment: FrameAlignment;
  /** The decoration is in an `if`: aligned by its edges, its `.alignmentGuide`s unused (absent otherwise). */
  conditional?: boolean;
}
// Phase 6 kinds (docs/phase6/chrome.md).

/**
 * `Menu { items } label: { label }`: children are `[label, ...items]`. Only
 * the label is laid out (the element is the label's size); tapping it opens
 * a popover built from the current items (`button`/`navlink` rows that send
 * `tap`, `picker` sections that send `select`, `divider`, `section`, `text`).
 */
/**
 * Lazy menus: `deferred: true` while the items are not built (only the label
 * is a child). The page sends a `menu` event with `open: true` when the
 * viewer opens it and shows the popover once the items arrive (`deferred:
 * false`), and `open: false` when it closes. Absent when the app builds the
 * items eagerly (headless runs, the mocks).
 */
export interface MenuProps {
  deferred?: boolean;
}
/**
 * `largeTitle` (iOS 26 `.largeTitle`): the items take the large title row in
 * place of the title's text, scrolling with the content as the title does;
 * the bar shows the title once the row has gone under it. Hidden while the
 * screen's title is inline. `keyboard` (`.keyboard`): a 44 bar across the
 * screen above the on-screen keyboard while a text input of the screen is
 * focused (the input accessory view), its items in a row from the leading
 * edge, a `spacer` child flexible; hidden otherwise.
 */
export type ToolbarPlacement = 'trailing' | 'leading' | 'principal' | 'bottom' | 'secondary' | 'largeTitle' | 'keyboard';
/**
 * `.toolbar { ToolbarItem(placement:) { ... } }`, an extra child of a
 * `navscreen` after its content. The children are the items, laid out at
 * their ideal size in a 16pt-spaced hstack inside the navigation bar
 * (`bottom`: a 49pt bar plus the bottom safe area, items spread evenly).
 */
export interface ToolbarProps {
  placement: ToolbarPlacement;
  /**
   * A `ToolbarSpacer` (no children): it separates glass groups, and in a
   * bottom bar flexible ones share the room left over and push the groups
   * apart; fixed ones are 8 wide.
   */
  spacer?: 'flexible' | 'fixed';
  /**
   * `.sharedBackgroundVisibility(.hidden)`: a leading or trailing group of
   * the navigation bar draws no glass of the bar's and is a run of its own
   * (a segmented picker is drawn as its own glass capsule either way).
   */
  sharedBackground?: 'hidden';
}
/**
 * `.searchable(text:)`, an extra child of a `navscreen`: a 36pt rounded
 * search bar under the title area (52pt added to the bar). Typing sends
 * `text` events exactly like a `textfield`.
 */
export interface SearchFieldProps {
  text: string;
  /** Defaults to "Search". */
  placeholder: string;
}
/**
 * `.onTapGesture`: one child, the child's size; a click anywhere on it sends
 * `tap`. `location` (`onTapGesture { point in }`) adds the click's point.
 */
export interface TapGestureProps {
  location?: boolean;
  /**
   * `.onTapGesture(count:)`: the taps required, 1 when absent. With 2 the
   * element sends one `tap` for two clicks within the double-tap interval
   * (300 ms); a `tapgesture` of one tap around or inside it waits that
   * interval before sending its own, as iOS delays a single tap while a
   * double tap could still land (docs/ops-protocol.md, "Gesture spaces and
   * composition").
   */
  count?: number;
}

/**
 * A box the page fills with its own DOM (src/hostContent.ts): `content` is
 * the key the page registered its element under. `sizing: "fill"` takes the
 * proposal, the content's own size on an unconstrained axis; `"content"`
 * takes the content's size, its width capped by the proposal and its height
 * measured at that width, like text. Only page-side producers (such as
 * @swiftbrowser/react) emit it; the Swift core never does.
 */
export interface HostProps {
  content: string;
  sizing: 'fill' | 'content';
}

export interface ElementPropsByKind {
  text: TextProps;
  vstack: VStackProps;
  hstack: HStackProps;
  spacer: SpacerProps;
  button: ButtonProps;
  styled: StyledProps;
  zstack: ZStackProps;
  image: ImageProps;
  divider: DividerProps;
  scrollview: ScrollViewProps;
  list: ListProps;
  section: SectionProps;
  disclosuregroup: DisclosureGroupProps;
  textfield: TextFieldProps;
  toggle: ToggleProps;
  navstack: NavStackProps;
  navscreen: NavScreenProps;
  navlink: NavLinkProps;
  color: ColorProps;
  shape: ShapeProps;
  sheet: SheetProps;
  tabview: TabViewProps;
  tab: TabProps;
  picker: PickerProps;
  geometry: GeometryProps;
  layout: LayoutProps;
  grid: GridProps;
  gridrow: GridRowProps;
  lazyvgrid: LazyVGridProps;
  lazyhgrid: LazyHGridProps;
  gauge: GaugeProps;
  decorated: DecoratedProps;
  menu: MenuProps;
  toolbar: ToolbarProps;
  searchfield: SearchFieldProps;
  tapgesture: TapGestureProps;
  host: HostProps;
  registered: RegisteredProps;
}

/**
 * A view a package registered under `name` with `ViewRegistry`: on a page
 * its own DOM (src/viewRegistry.ts), in a native app a SwiftUI view. It
 * takes the proposal, 10 on an unconstrained axis, as `GeometryReader`
 * does; its children, if any, are the native view's to place.
 */
export interface RegisteredProps {
  name: string;
  /** JSON, as the app wrote it, for the view. */
  props: Record<string, unknown>;
}

export type ElementKind = keyof ElementPropsByKind;

export type ElementProps = ElementPropsByKind[ElementKind];

/** Discriminated `kind` + matching `props` pair. */
export type ElementDescriptor = {
  [K in ElementKind]: { kind: K; props: ElementPropsByKind[K] };
}[ElementKind];

// ---------------------------------------------------------------------------
// Ops
// ---------------------------------------------------------------------------

export type CreateOp = { op: 'create'; id: ElementId } & ElementDescriptor;

export interface UpdateOp {
  op: 'update';
  id: ElementId;
  /** Replaces the element's props entirely. */
  props: ElementProps;
}

export interface InsertOp {
  op: 'insert';
  parent: ElementId;
  id: ElementId;
  /** Insert as child `index` of `parent`, moving the element if already attached. */
  index: number;
}

export interface RemoveOp {
  op: 'remove';
  id: ElementId;
}

/**
 * End of a render pass. Renderers may batch DOM work until this. With
 * `animation` (from `withAnimation`) every frame and style change of the pass
 * animates, inserted elements transition in and removed ones out.
 */
export interface CommitOp {
  op: 'commit';
  animation?: Animation;
}

/**
 * Phase 11: the app's `UserDefaults`, whole, after a change. The host keeps
 * them (localStorage, per app) and hands them back at the next launch through
 * the `SB_DEFAULTS` environment variable. Not a rendering op: the loader
 * consumes it before the renderer sees the batch.
 */
export interface DefaultsOp {
  op: 'defaults';
  values: Record<string, unknown>;
}

/**
 * Phase 13: something only the page can do for the app. The loader performs
 * it (src/hostRequests.ts) and answers with a `ResponseEvent` of the same
 * `id`; the renderer never sees it.
 */
export interface RequestOp {
  op: 'request';
  id: number;
  kind: 'fetch' | 'image' | 'share' | 'pickFiles' | 'exportFiles' | 'notification' | 'scanner' | 'websocket' | 'openURL' | 'webAuth' | 'appleSignIn' | 'media' | 'download' | 'clipboard' | 'translate' | 'pickColor' | 'mediaSession' | 'undo';
  params:
    | FetchRequestParams
    | ImageRequestParams
    | ShareRequestParams
    | PickFilesRequestParams
    | ExportFilesRequestParams
    | NotificationRequestParams
    | ScannerRequestParams
    | WebSocketRequestParams
    | OpenURLRequestParams
    | WebAuthRequestParams
    | AppleSignInRequestParams
    | MediaRequestParams
    | DownloadRequestParams
    | ClipboardRequestParams
    | TranslateRequestParams
    | PickColorRequestParams
    | MediaSessionRequestParams
    | UndoRequestParams;
}
/**
 * `undo` (docs/ops-protocol.md, "Undo"): what the Swift app's `UndoManager`
 * can do, after a turn that changed it, for the page's undo host
 * (src/undo.ts). Answered `{}`; the app does not wait for it. The page's
 * ⌘Z and ⇧⌘Z come back as `UndoEvent`s.
 */
export interface UndoRequestParams {
  canUndo: boolean;
  canRedo: boolean;
  /** The name of what undo takes back, '' for none. */
  undoActionName: string;
  redoActionName: string;
}
/**
 * `translate` (Translation; src/translation.ts): `availability` of a pair
 * (`source` null: detected from `text`), or `translate` the `texts` from
 * `source` (null: detected) to `target`, BCP 47 tags. `allowDownload`: the
 * session may download the pair's model.
 */
export interface TranslateRequestParams {
  action: 'availability' | 'translate';
  source?: string | null;
  target: string;
  text?: string;
  texts?: string[];
  allowDownload?: boolean;
}
/** `pickColor` (`ColorPicker`; src/colorPicker.ts): the chooser opens at `color` (`#rrggbb`). */
export interface PickColorRequestParams {
  color?: string;
}
/**
 * `URLSessionWebSocketTask`: one call on a socket (src/webSocket.ts). `open`
 * takes `url` and `protocols`; the others the `socket` id `open` answered.
 */
/** `openURL` (`OpenURLAction`, a link in a `Text`, `UIApplication.open`): the page opens `url` in a new tab; the value is null. */
/**
 * `webAuth` (AuthenticationServices; docs/ops-protocol.md, "Signing in"):
 * `start` opens `url`, the provider's authorize page, in a popup window and
 * answers `{ url }`, the callback page URL the window ended at; `cancel` closes
 * the window, and the pending `start` fails with `cancelled: …`.
 * `callbackScheme` is the app's scheme the redirect stands for, `callbackPage`
 * the callback page the app put in its place; `ephemeral` is the app's wish
 * for a private session, which a popup cannot grant.
 */
export interface WebAuthRequestParams {
  action?: 'start' | 'cancel';
  url?: string;
  callbackScheme?: string;
  callbackPage?: string;
  ephemeral?: boolean;
}
/**
 * Sign in with Apple (docs/ops-protocol.md, "Sign in with Apple"): `signIn`
 * asks for the viewer's Apple ID with `scopes` (`fullName`, `email`), a
 * `nonce` for the identity token and a `state` echoed back; the response
 * value is an `AppleIDCredential`, or the request fails `cancelled: …`.
 * `credentialState` asks what a `user` identifier's credential is now; the
 * response value is `{ state }` (`authorized`, `revoked`, `notFound`,
 * `transferred`, or `unknown` where the host cannot tell).
 */
export interface AppleSignInRequestParams {
  action: 'signIn' | 'credentialState';
  scopes?: ('fullName' | 'email')[];
  nonce?: string;
  state?: string;
  user?: string;
}

export interface OpenURLRequestParams {
  url: string;
}
export interface WebSocketRequestParams {
  action: 'open' | 'send' | 'receive' | 'close';
  url?: string;
  protocols?: string[];
  socket?: number;
  /** `send`: a text message. */
  string?: string;
  /** `send`: a binary message, base64. */
  data?: string;
  /** `close`: the close code and reason. */
  code?: number;
  reason?: string;
}
/** `URLSession`: one HTTP request. `body` is base64; `timeout` in ms (0: none). */
export interface FetchRequestParams {
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string | null;
  timeout?: number;
}
/** `AsyncImage`: decode the image at `url` and report its pixel size. */
export interface ImageRequestParams {
  url: string;
}

/**
 * `ShareLink`: share through the Web Share API, or download where the browser
 * cannot. `images` are asset-catalog names, shared (or downloaded) as their
 * files. The response value is a `ShareResult`.
 */
export interface ShareRequestParams {
  title?: string;
  text?: string;
  url?: string;
  images?: string[];
  /** `Image(uiImage:)` items: recipes the page draws into PNG files. */
  bitmaps?: BitmapRecipe[];
  /** Files the app made: a `Transferable` item's exported bytes, a `UIImage`'s own file. */
  files?: SharedFile[];
}

/** A file the app hands to the page whole: its name, MIME type and bytes (base64). */
export interface SharedFile {
  name: string;
  type: string;
  data: string;
}

/**
 * Saving (Photos' change requests, `UIImageWriteToSavedPhotosAlbum`): the
 * page downloads each file, catalog image and bitmap (a PNG named after
 * `title`). The response value is a `ShareResult` (`downloaded`).
 */
export interface DownloadRequestParams {
  title?: string;
  files?: SharedFile[];
  images?: string[];
  bitmaps?: BitmapRecipe[];
}

/**
 * `UIPasteboard.general`: `write` copies `text`, or the first picture (a
 * file, catalog image or bitmap, as PNG) to the system clipboard and answers
 * `{}`; `readText` answers `{ text }`.
 */
export interface ClipboardRequestParams {
  action: 'write' | 'readText';
  text?: string;
  files?: SharedFile[];
  images?: string[];
  bitmaps?: BitmapRecipe[];
}

/**
 * `PhotosPicker`: let the viewer choose files with the browser's file
 * chooser. `accept` is the input's `accept` (`image/*,video/*`; empty: any
 * file). The response value is a `PickFilesResult`; a dismissed chooser
 * answers with no items.
 */
export interface PickFilesRequestParams {
  accept: string;
  multiple: boolean;
}

/**
 * `fileExporter`, saving to Files: let the viewer choose where the app's
 * files go. The page shows a save dialog where the browser has one (the
 * File System Access API) and downloads the files elsewhere; the native app
 * shows the document picker. The response value is an `ExportFilesResult`.
 */
export interface ExportFilesRequestParams {
  files: SharedFile[];
}

/**
 * UserNotifications (docs/ops-protocol.md, "Notifications"): `status` reads
 * `Notification.permission`, `request` asks for it, `show` presents a
 * notification that came due (`title` absent: the badge only), `close`
 * closes delivered ones by identifier, `badge` sets the app badge. The
 * response value is a `NotificationResult`.
 */
export interface NotificationRequestParams {
  action: 'status' | 'request' | 'show' | 'close' | 'badge' | 'pushSubscribe' | 'pushUnsubscribe' | 'receive';
  /** `show`: the request's identifier, the notification's `tag`. */
  id?: string;
  title?: string;
  subtitle?: string;
  body?: string;
  silent?: boolean;
  /** `show` and `badge`: the app badge (0 clears it). */
  badge?: number;
  /** `close`: the identifiers to close. */
  ids?: string[];
  /** `pushSubscribe`: the Web Push application server key (a VAPID public key, base64url). */
  applicationServerKey?: string;
}

/**
 * AVFoundation's capture sessions (docs/ops-protocol.md, "Code scanning"):
 * `probe` asks whether the page can scan `formats` (`BarcodeDetector` names)
 * with a camera; `scan` opens the camera over the page until a code is found
 * or the viewer closes it, or, where the browser cannot read codes, asks the
 * viewer to type one. The response value is a `ScannerResult`.
 */
export interface ScannerRequestParams {
  action: 'probe' | 'scan';
  formats: string[];
  torch?: boolean;
  vibrate?: boolean;
  /** Which camera: the back one (`environment`, the default) or the front one (`user`). */
  facing?: 'environment' | 'user';
}

/**
 * Phase 18: one save of a SwiftData store (`ModelContext.save()`, or the main
 * context's autosave at the end of an event). The loader hands it to the page,
 * which writes it to the app's IndexedDB database (src/swiftData.ts) and
 * passes every store back at the next launch as `SB_SWIFTDATA`. `clear`
 * (`ModelContainer.deleteAllData()`) empties the store before `put`.
 */
export interface SwiftDataOp {
  op: 'swiftdata';
  store: string;
  put: SwiftDataRecord[];
  delete: number[];
  clear?: boolean;
}

/** A saved model: its key in the store, its entity (class) name and its properties' JSON. */
export interface SwiftDataRecord {
  id: number;
  entity: string;
  values: Record<string, unknown>;
}

/**
 * A bitmap's source, sent once (by content `key`) before the first element
 * that draws it: an encoded image file (`mime`, `data`) or a pixel buffer
 * (`format`, `pixels`: one gray or four RGBA bytes per pixel, rows from the
 * top). The bytes are base64 in printed ops; from a module with the
 * `sb_bytes_*` exports, src/wasm.ts reads them out of linear memory
 * (docs/ops-protocol.md, "Bytes"). `width` × `height` is its size in pixels, as
 * drawn (a JPEG's EXIF rotation applied). The renderer keeps it for the run.
 */
export interface ImageDataOp {
  op: 'imagedata';
  key: string;
  width: number;
  height: number;
  mime?: string;
  data?: string | Uint8Array;
  format?: 'gray' | 'rgba';
  pixels?: string | Uint8Array;
}

export type Op = CreateOp | UpdateOp | InsertOp | RemoveOp | CommitOp | DefaultsOp | RequestOp | SwiftDataOp | ImageDataOp;

// ---------------------------------------------------------------------------
// Events (renderer → Swift)
// ---------------------------------------------------------------------------

export type ColorScheme = 'light' | 'dark';

export type DynamicTypeSize =
  | 'xSmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'xLarge'
  | 'xxLarge'
  | 'xxxLarge'
  | 'accessibility1'
  | 'accessibility2'
  | 'accessibility3'
  | 'accessibility4'
  | 'accessibility5';

export const DYNAMIC_TYPE_SIZES: readonly DynamicTypeSize[] = [
  'xSmall',
  'small',
  'medium',
  'large',
  'xLarge',
  'xxLarge',
  'xxxLarge',
  'accessibility1',
  'accessibility2',
  'accessibility3',
  'accessibility4',
  'accessibility5',
];

export function isDynamicTypeSize(value: unknown): value is DynamicTypeSize {
  return typeof value === 'string' && (DYNAMIC_TYPE_SIZES as readonly string[]).includes(value);
}

/**
 * A `button`, `navlink`, `tapgesture`, (for the back button) `navscreen` or
 * (for a dismiss request) `sheet` was tapped.
 */
export interface TapEvent {
  type: 'tap';
  id: ElementId;
  /** A `tapgesture` with `location`: the tap's point in the element's own coordinates. */
  x?: number;
  y?: number;
}
/** A link run (`TextRun.link`) of a `text` was tapped; `value` is its URL. */
export interface LinkEvent {
  type: 'link';
  id: ElementId;
  value: string;
}
/** A `textfield` or `searchfield` received input. */
export interface TextEvent {
  type: 'text';
  id: ElementId;
  value: string;
}
/** A `toggle` was switched. */
export interface ToggleEvent {
  type: 'toggle';
  id: ElementId;
  value: boolean;
}
/**
 * The device appearance or text size changed (also sent once right after
 * `_start`). Either field may be omitted; this renderer always sends both.
 */
export interface EnvironmentEvent {
  type: 'environment';
  colorScheme?: ColorScheme;
  dynamicTypeSize?: DynamicTypeSize;
}

/** A `tabview` bar item or a `picker` option was chosen; `value` is the index. */
export interface SelectEvent {
  type: 'select';
  id: ElementId;
  value: number;
}
/**
 * A `geometry` element's laid-out size (whole CSS px), sent after a layout
 * pass only when it differs from the last reported size.
 */
export interface GeometryEvent {
  type: 'geometry';
  id: ElementId;
  width: number;
  height: number;
  /** With `reportsFrame`: the box's origin in the root's coordinates, scrolling included (whole px). */
  x?: number;
  y?: number;
  /** With `reportsFrame`: how far the device's safe area reaches into the box on each edge. */
  safeArea?: { top: number; leading: number; bottom: number; trailing: number };
  /**
   * With `reportsFrame`, inside a scroll view or list: its visible rectangle
   * in the root's coordinates (whole px) and the axis it scrolls along.
   */
  scrollView?: { x: number; y: number; width: number; height: number; axis: 'horizontal' | 'vertical' };
}

/** Host measurements of one subview of a `layout` element (points, 2 decimals; `"infinity"` for an unbounded axis). */
export interface LayoutChildMeasurement {
  min: { width: Dimension; height: Dimension };
  ideal: { width: Dimension; height: Dimension };
  max: { width: Dimension; height: Dimension };
}
/**
 * Phase 6: sent after a layout pass for every `layout` element whose record
 * (the proposal it was placed with and its children's min / ideal / max sizes)
 * differs from the last one sent for that id. Swift answers by updating the
 * element's `size` and `frames` props.
 */
export interface LayoutEvent {
  type: 'layout';
  id: ElementId;
  proposal: LayoutProposal;
  children: LayoutChildMeasurement[];
}

/**
 * Everything the renderer sends to Swift. Taps travel through `sb_event(id)`,
 * the rest as UTF-8 JSON through `sb_alloc` + `sb_event_json`.
 */
export type RendererEvent =
  | TapEvent
  | TextEvent
  | ToggleEvent
  | EnvironmentEvent
  | SelectEvent
  | LinkEvent
  | GeometryEvent
  | LayoutEvent
  | BatchEvent
  // Phase 8: gestures, rows, navigation (see the block at the end of this file)
  | InteractionEvent
  // Phase 8: alerts, controls (see the block at the end of this file)
  | Phase8ControlEvent
  // Phase 13: the answer to a `request` op
  | ResponseEvent
  // A `video` element's playback (src/video.ts)
  | VideoEvent
  | LazyEvent
  | MenuEvent
  | HoverEvent
  | ContinuousHoverEvent
  // Scroll geometry and animation completion (the block at the end of this file)
  | ScrollGeometryEvent
  | AnimationCompleteEvent
  // The page's undo and redo keys, for the app's undo manager (src/undo.ts)
  | UndoEvent
  // A registered view's own answer (src/viewRegistry.ts)
  | RegisteredEvent
  // A stack's path set whole by the tree that hosts this one (@swiftbrowser/react RemoteTree)
  | SetPathEvent
  // A Mac split view's sidebar dragged to a new width
  | ColumnWidthEvent;

/**
 * A `navsplit`'s sidebar was dragged to a new width (a Mac's column
 * divider): the width it is laid out at once the drag ends, within the
 * column's bounds. Sent once a drag, so an app can keep it (and start the
 * sidebar there next time, as `ideal`).
 */
export interface ColumnWidthEvent {
  type: 'columnwidth';
  id: ElementId;
  width: number;
}

/**
 * A `navstack`'s path set whole: the values its destinations build the
 * screens from, as its pushed `navscreen`s report them (`route`). Sent by a
 * host drawing this tree (`RemoteTree`), to reopen the screens a link named;
 * never by a renderer.
 */
export interface SetPathEvent {
  type: 'setpath';
  id: ElementId;
  path: unknown[];
}

/** What a registered view sent (`context.send(value)`), for the element's `onEvent`. */
export interface RegisteredEvent {
  type: 'registered';
  id: ElementId;
  name: string;
  value: unknown;
}

/**
 * Phase 13: the page's answer to the `request` op with this `id`: `value`
 * (a `FetchResult` or `ImageResult` from src/hostRequests.ts) on success,
 * `error` (a short message the app maps to a `URLError`) on failure.
 */
export interface ResponseEvent {
  type: 'response';
  id: number;
  value?: unknown;
  error?: string;
}

/**
 * Several events delivered in one `sb_event_json` call: Swift applies each
 * and renders once. The renderer sends one per layout pass for the pass's
 * `geometry` and `layout` events, so a screen with many measured elements
 * costs one render instead of one per element. Never nested.
 */
export interface BatchEvent {
  type: 'batch';
  events: Exclude<RendererEvent, BatchEvent>[];
}

/** Headless-mode echo of a replayed event; never sent to the browser renderer. */
export type EventEchoOp = { op: 'event' } & RendererEvent;

export const ROOT_ID: ElementId = 0;

/** `spacing: null` means "system default", which the renderer treats as 8px. */
export const DEFAULT_SPACING = 8;

export function isNamedColor(color: ShapeStyle): color is NamedColor {
  return typeof (color as NamedColor).name === 'string';
}

export function isAssetColor(color: ShapeStyle): color is AssetColor {
  return typeof (color as AssetColor).named === 'string';
}

export function isRGBAColor(style: ShapeStyle): style is RGBAColor {
  const c = style as RGBAColor;
  return typeof c.r === 'number' && typeof c.g === 'number' && typeof c.b === 'number';
}

/** True for the three `Color` forms (explicit, semantic, catalog). */
export function isColor(style: ShapeStyle): style is Color {
  return isRGBAColor(style) || isNamedColor(style) || isAssetColor(style);
}

export function isHierarchicalStyle(style: ShapeStyle): style is HierarchicalStyle {
  return typeof (style as HierarchicalStyle).hierarchical === 'number';
}

export function isLinearGradientStyle(style: ShapeStyle): style is LinearGradientStyle {
  const g = (style as LinearGradientStyle).linearGradient;
  return typeof g === 'object' && g !== null;
}

export function isRadialGradientStyle(style: ShapeStyle): style is RadialGradientStyle {
  const g = (style as RadialGradientStyle).radialGradient;
  return typeof g === 'object' && g !== null;
}

export function isAngularGradientStyle(style: ShapeStyle): style is AngularGradientStyle {
  const g = (style as AngularGradientStyle).angularGradient;
  return typeof g === 'object' && g !== null;
}

export function isColorGradientStyle(style: ShapeStyle): style is ColorGradientStyle {
  const c = (style as ColorGradientStyle).colorGradient;
  return typeof c === 'object' && c !== null;
}

export function isMaterialStyle(style: ShapeStyle): style is MaterialStyle {
  return typeof (style as MaterialStyle).material === 'string';
}

/** True for the asset catalog form of `image` props (`{ name, resizable }`). */
export function isAssetImage(props: ImageProps): props is AssetImageProps {
  return typeof (props as AssetImageProps).name === 'string';
}

/** True for the bitmap form of `image` props (`{ bitmap, resizable, width, height }`). */
export function isBitmapImage(props: ImageProps): props is BitmapImageProps {
  const bitmap = (props as BitmapImageProps).bitmap;
  return typeof bitmap === 'object' && bitmap !== null;
}

/** True for the remote form of `image` props (`{ url, resizable, width, height }`, Phase 13). */
export function isUrlImage(props: ImageProps): props is UrlImageProps {
  return typeof (props as UrlImageProps).url === 'string';
}

/** Cheap structural check used when parsing an untrusted JSON buffer. */
export function isOp(value: unknown): value is Op {
  if (typeof value !== 'object' || value === null) return false;
  const op = (value as { op?: unknown }).op;
  return (
    op === 'create' ||
    op === 'update' ||
    op === 'insert' ||
    op === 'remove' ||
    op === 'commit' ||
    op === 'defaults' ||
    op === 'request' ||
    op === 'swiftdata' ||
    op === 'imagedata'
  );
}

// ---------------------------------------------------------------------------
// Phase 7: navigation, ViewThatFits, styles
// (docs/ops-protocol.md, "Phase 7"; charts live in their own block)
// ---------------------------------------------------------------------------

/** `NavigationSplitView` in the regular size class: children are `[sidebar, detail]`, each a `navstack`. */
export interface NavSplitProps {
  /** iPadOS: false hides the sidebar (`NavigationSplitViewVisibility.detailOnly`); absent or true shows it. */
  sidebarVisible?: boolean;
}

export type ViewThatFitsAxes = 'horizontal' | 'vertical' | 'both';
/**
 * `ViewThatFits`: the children are the candidates, in order. The engine shows
 * the first whose ideal size fits the proposal on `axes` (default `both`), or
 * the last when none fits; the others get no frame and are hidden.
 */
export interface ViewThatFitsProps {
  axes?: ViewThatFitsAxes;
}

export interface ElementPropsByKind {
  navsplit: NavSplitProps;
  viewthatfits: ViewThatFitsProps;
  inspector: InspectorProps;
  inspectorpanel: Record<string, never>;
}

/**
 * `.inspector(isPresented:)` in the regular size class: the children are the
 * content, then an `inspectorpanel` (the trailing column's content).
 */
export interface InspectorProps {
  /** Whether the panel is shown; false slides it off the trailing edge. */
  presented: boolean;
}

export interface Style {
  /** `.hidden()`: the box keeps its layout but draws nothing and takes no input. */
  hidden?: true;
  /**
   * `.mask { Gradient / Color }`: a CSS `mask-image` built from the style
   * (gradients as CSS gradients, a `Color` as a solid mask with its alpha).
   */
  mask?: ShapeStyle;
}

/** `UserInterfaceSizeClass`: `regular` when the screen is at least 700 CSS px wide. */
export type HorizontalSizeClass = 'compact' | 'regular';
/** Threshold (CSS px) between the compact and regular horizontal size class. */
export const REGULAR_WIDTH_MIN = 700;

export interface EnvironmentEvent {
  horizontalSizeClass?: HorizontalSizeClass;
}

/** The `horizontalSizeClass` for a screen `width` CSS px wide. */
export function horizontalSizeClassFor(width: number): HorizontalSizeClass {
  return Number.isFinite(width) && width >= REGULAR_WIDTH_MIN ? 'regular' : 'compact';
}

/**
 * The platform the app is drawn as (docs/notes/macos.md): `ios`, the
 * default, or `macos`. The renderer draws the platform's idiom and reports
 * it in the first `environment` event; it never changes in a session.
 */
export type Platform = 'ios' | 'macos';

export interface EnvironmentEvent {
  platform?: Platform;
}

// ---------------------------------------------------------------------------
// Phase 7: charts (docs/ops-protocol.md, "Phase 7"). Swift turns data into
// unit coordinates (0...1 of the plot rectangle, y top → bottom); the renderer
// measures labels, derives the plot rectangle and draws the marks as SVG.
// ---------------------------------------------------------------------------

export type ChartInterpolation =
  | 'linear'
  | 'cardinal'
  | 'catmullRom'
  | 'monotone'
  | 'stepStart'
  | 'stepCenter'
  | 'stepEnd';

export type ChartSymbol = 'circle' | 'square' | 'triangle' | 'diamond' | 'cross' | 'plus' | 'asterisk';

/** Fields every mark carries. */
export interface ChartMarkBase {
  style: ShapeStyle;
  /** 0...1, default 1. */
  opacity?: number;
  /** An `area` or `rect` mark whose geometry clips this mark (an SVG `clipPath`). */
  mask?: ChartMark | null;
}
/** `BarMark` / `RectangleMark`: a filled `<rect>` over the unit rect. */
export interface ChartRectMark extends ChartMarkBase {
  kind: 'rect';
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  /** Px, default 0. */
  cornerRadius?: number;
  /** Px: the rect is this wide, centered on `(x0 + x1) / 2`. */
  fixedWidth?: number | null;
}
export interface ChartLinePoint {
  x: number;
  y: number;
}
/** `LineMark`: a stroked `<path>` plus one symbol glyph per point. */
export interface ChartLineMark extends ChartMarkBase {
  kind: 'line';
  points: ChartLinePoint[];
  /** Px, default 2. */
  lineWidth?: number;
  interpolation?: ChartInterpolation;
  symbol?: ChartSymbol | null;
  /** Px, default 8. */
  symbolSize?: number;
}
export interface ChartAreaPoint {
  x: number;
  /** `y0 <= y1` (y runs top → bottom, so `y0` is the upper edge). */
  y0: number;
  y1: number;
}
/** `AreaMark`: a filled `<path>` between the two curves. */
export interface ChartAreaMark extends ChartMarkBase {
  kind: 'area';
  points: ChartAreaPoint[];
  interpolation?: ChartInterpolation;
}
/** `PointMark`: one symbol glyph. */
export interface ChartPointMark extends ChartMarkBase {
  kind: 'point';
  x: number;
  y: number;
  symbol: ChartSymbol;
  /** Px, default 8. */
  symbolSize?: number;
}
/** `RuleMark`: a stroked line between the two points. */
export interface ChartRuleMark extends ChartMarkBase {
  kind: 'rule';
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  /** Px, default 1. */
  lineWidth?: number;
}
export type ChartMark = ChartRectMark | ChartLineMark | ChartAreaMark | ChartPointMark | ChartRuleMark;

export type ChartXAxisPosition = 'bottom' | 'top';
export type ChartYAxisPosition = 'trailing' | 'leading';
export interface ChartAxisLabel {
  /** Unit position along the axis (y: 0 is the top). */
  at: number;
  text: string;
  /** `leading`: an x label that starts at its tick (a date's) instead of centering on it. */
  anchor?: 'leading';
}
/** `null` for the whole axis means hidden. Positions are unit values along the axis. */
export interface ChartAxis {
  position: ChartXAxisPosition | ChartYAxisPosition;
  gridLines: number[];
  /** A date axis: its grid lines are dashed (2pt on, 2pt off), as Swift Charts draws them. */
  dashed?: true;
  ticks: number[];
  /** Default text labels; view labels are `chartitem` children with role `xLabel` / `yLabel`. */
  labels: ChartAxisLabel[];
}

export type ChartLegendPosition = 'top' | 'bottom' | 'leading' | 'trailing';
export interface ChartLegendItem {
  label: string;
  style: ShapeStyle;
  /** `null`: an 18 × 3 line glyph instead of a symbol. */
  symbol: ChartSymbol | null;
}
export interface ChartLegend {
  position: ChartLegendPosition;
  items: ChartLegendItem[];
}

/** `Chart`: children are `chartitem`s (annotations and view axis labels), in any order. */
export interface ChartProps {
  marks: ChartMark[];
  xAxis: ChartAxis | null;
  yAxis: ChartAxis | null;
  legend: ChartLegend | null;
}

export type ChartItemRole = 'annotation' | 'xLabel' | 'yLabel';
export type ChartItemPosition = 'top' | 'bottom' | 'leading' | 'trailing' | 'overlay';
/**
 * One view inside a `chart`: an annotation placed against the unit rect
 * `x0…x1 × y0…y1` of its mark, or an axis label view whose tick is
 * `x0 == x1` (x) / `y0 == y1` (y). Exactly one child.
 */
export interface ChartItemProps {
  role: ChartItemRole;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  position: ChartItemPosition;
  alignment: FrameAlignment;
  /** Px between the mark's rect and the annotation (default 0). */
  spacing: number;
}

// Declaration merging adds the Phase 7 kinds to `ElementPropsByKind` (and so
// to `ElementKind`, `ElementProps`, `ElementDescriptor` and `CreateOp`)
// without touching the Phase 1-6 table above.
export interface ElementPropsByKind {
  chart: ChartProps;
  chartitem: ChartItemProps;
}

export const CHART_KINDS: readonly ElementKind[] = ['chart', 'chartitem'];

// ---------------------------------------------------------------------------
// Phase 8: gestures, rows, navigation (docs/ops-protocol.md, "Phase 8:
// interaction"). `gesture`, `swipeactions`, `contextmenu` and `listrow` are
// layout-transparent wrappers; the renderer tracks pointers on them
// (src/interaction.ts). Declaration merging adds the kinds, the `Style` hint
// and the `list` / `scrollview` fields without touching the tables above.
// ---------------------------------------------------------------------------

/**
 * `.gesture(DragGesture())` / `.onLongPressGesture`: exactly one child, the
 * child's size. With `drag`, a pointer that moves `minimumDistance` (default
 * 10, 0 for an immediate gesture) starts a drag and the element receives
 * `drag` events; with `longPress`, a pointer held `minimumDuration` (default
 * 0.5 s) without moving 10 px sends `longpress`.
 */
export interface GestureProps {
  drag?: boolean;
  longPress?: boolean;
  minimumDistance?: number;
  minimumDuration?: number;
  /**
   * `MagnifyGesture` / `RotateGesture` (src/pinch.ts): two fingers on the
   * element, a trackpad pinch (a wheel with ctrl, or Safari's gesture events
   * on a computer) send `pinch` events once the scale has changed by
   * `minimumScaleDelta` (default 0.01, with `magnify`) or the angle by
   * `minimumAngleDelta` degrees (default 1, with `rotate`).
   */
  magnify?: boolean;
  rotate?: boolean;
  minimumScaleDelta?: number;
  minimumAngleDelta?: number;
  /**
   * `DragGesture(coordinateSpace:)`: the space of a `drag` event's `location`
   * and `startLocation`. `local` (the default) is the element's own box;
   * `global` the root's coordinates, scrolling included, the same a
   * `reportsFrame` geometry event reports, so a named space is the global
   * point less that space's reported origin.
   */
  coordinateSpace?: GestureCoordinateSpace;
  /**
   * How the gesture composes with the others on the pointer's way to the
   * root (src/interaction.ts). Absent, `.gesture`: the innermost gesture
   * whose motion matches wins. `highPriority` (`.highPriorityGesture`): asked
   * before the gestures of the views inside it. `simultaneous`
   * (`.simultaneousGesture`): begins when its own distance is met and runs
   * beside whatever else owns the pointer, never claiming it from them.
   */
  mode?: GestureMode;
}

export type GestureCoordinateSpace = 'local' | 'global';
export type GestureMode = 'simultaneous' | 'highPriority';

/**
 * `.swipeActions`: children are `[content, ...trailing actions, ...leading
 * actions]` (`trailing` and `leading` count them); the actions are `button`s
 * (a `tint` style colors one, role `destructive` is red). A horizontal pan on
 * the row reveals 74 pt buttons behind the content; tapping one sends `tap`
 * with the button's id. A full swipe (allowed by default) past the actions'
 * width + 60 performs the first declared action of that edge.
 */
export interface SwipeActionsProps {
  trailing: number;
  leading: number;
  fullSwipeTrailing?: boolean;
  fullSwipeLeading?: boolean;
}

/** `.contextMenu { }`: `[content, ...items]`; a long press or right click lists the items (as in a `menu`) in a popover. */
/** As `MenuProps`: `deferred` while the items (every child but the content) are not built. */
export interface ContextMenuProps {
  deferred?: boolean;
}

/**
 * A `List` row that can be selected (`selectable`, `selected`), deleted
 * (`deletable`, from `ForEach.onDelete`) or moved (`movable`, from
 * `ForEach.onMove`; absent means false). A tap on a selectable row sends
 * `tap` with the row id; swipe-to-delete or the edit-mode minus sends
 * `delete`. A movable row shows a grip at its trailing edge while the list
 * is editing, and a long press lifts it otherwise; dragging it among its
 * neighbours and letting go sends `move` (src/dragDrop.ts).
 */
export interface ListRowProps {
  selectable?: boolean;
  selected?: boolean;
  deletable?: boolean;
  movable?: boolean;
}

/**
 * `.draggable` / `.onDrag`: exactly one child, the child's size. A long
 * press (touch) or a 10 px drag (mouse) lifts a copy of the content, which
 * follows the pointer; `dragstart` goes out as it lifts and `dragend` once
 * it is dropped or let go (src/dragDrop.ts).
 */
export type DraggableProps = Record<string, never>;

/**
 * `.dropDestination` / `.onDrop`: exactly one child, the child's size. A
 * lifted `draggable` over it sends `drop` with the phase `entered` /
 * `exited`, and `performed` with the source when released over it. Text,
 * links and files dragged in from outside the page (the browser's own drag
 * and drop) are offered when their MIME type matches one of `accepts`
 * (`text/plain`, `image/*`, or a star over a star for anything); they
 * arrive as `items`.
 */
export interface DropDestinationProps {
  accepts?: string[];
}

/**
 * `.onTouches` (a SwiftBrowser addition: SwiftUI has no multi-touch gesture):
 * exactly one child, the child's size. The element owns every pointer that
 * goes down on it and receives `touches` events with all of them, so an app
 * can hear chords and glissandi (a piano, a drum pad, a multi-finger
 * drawing). No props.
 */
export type TouchesProps = Record<string, never>;

export interface ElementPropsByKind {
  gesture: GestureProps;
  touches: TouchesProps;
  swipeactions: SwipeActionsProps;
  contextmenu: ContextMenuProps;
  listrow: ListRowProps;
  draggable: DraggableProps;
  dropdestination: DropDestinationProps;
}

export const INTERACTION_KINDS: readonly ElementKind[] = ['gesture', 'touches', 'swipeactions', 'contextmenu', 'listrow', 'draggable', 'dropdestination'];

export interface ListProps {
  /** `EditButton` / `editMode`: a selectable or deletable `listrow` gains a 44 pt leading control and its content shifts right 40; a movable one a trailing grip. */
  editing?: boolean;
  /** `.refreshable`: a pull past 60 pt at the top sends `refresh`. */
  refreshable?: boolean;
  /** Swift sets it while the refresh action runs; a 40 pt spinner row shows above the content. */
  refreshing?: boolean;
  /** `List(selection:)` over a `Set` (from `@swiftbrowser/react`): several rows may be selected; the native renderer shows the checkmark circles in edit mode. */
  multiSelect?: boolean;
}

export interface ScrollViewProps {
  refreshable?: boolean;
  refreshing?: boolean;
  /**
   * `.scrollIndicators(.hidden)` (or `ScrollView(showsIndicators: false)`):
   * false hides the scroll bars natively; the page draws none either way.
   */
  showsIndicators?: boolean;
  /** Phase 12 `.scrollTargetBehavior`: CSS scroll snapping on the axis. */
  snap?: 'viewAligned' | 'paging';
  /**
   * `.scrollClipDisabled()`: a horizontal scroll view's frame reaches the
   * screen's sides (chrome `viewport` is its own rect), so its content draws
   * past it while scrolling and snapping as before.
   */
  clipDisabled?: boolean;
  /**
   * `.scrollPosition(id:)`: when scrolling settles, the page sends
   * `scrollposition` to the scroll target (`scrollID`) at the view's anchor
   * point, once per change of target (docs/ops-protocol.md, "Scrolling to a view").
   */
  reportsPosition?: boolean;
  /** With `reportsPosition`: the point of the visible area the reported target is at; the leading edge (`{x: 0, y: 0}`) when omitted. */
  positionAnchor?: UnitPoint;
}

export interface Style {
  /** `.allowsHitTesting(false)`: `pointer-events: none` on the subtree. */
  hitTesting?: boolean;
  /**
   * `.contentShape(Rectangle())`: the whole box takes taps and gestures,
   * whatever is drawn in it (a clear fill, a stroke, the gaps between
   * views). Natively the modifier itself; the page's boxes already take
   * input across their area, so it only marks the box (`data-sb-content-shape`).
   */
  contentShape?: 'rectangle';
}

export interface Style {
  /**
   * `.onScrollVisibilityChange(threshold:_:)`: the page sends
   * `scrollvisibility` to the box when the share of it inside its scroll
   * view's visible area (below a bar the view scrolls under, above a bar
   * covering its bottom) crosses `threshold` (src/scrollVisibility.ts).
   */
  scrollVisibility?: { threshold: number };
}

/**
 * The effects a scroll transition draws in one phase: the visual-effect
 * fields of a `Style` (none of them changes the layout).
 */
export type ScrollTransitionEffect = Pick<
  Style,
  | 'offset'
  | 'scale'
  | 'scaleAnchor'
  | 'rotation'
  | 'rotationAnchor'
  | 'rotation3D'
  | 'opacity'
  | 'blur'
  | 'brightness'
  | 'contrast'
  | 'saturation'
  | 'grayscale'
  | 'hueRotation'
>;

/**
 * `.scrollTransition(_:axis:transition:)`: the transition's effects in its
 * three phases (its closure called once for each), which the renderer draws
 * as the box moves through its scroll view, without a round trip.
 */
export interface ScrollTransition {
  /**
   * `interactive`: between phases as the box crosses the scroll view's edge,
   * by how far it is out (0 wholly in view, 1 wholly out); `animated`: the
   * phase's effects, animated, once the box is partly out; `identity`: the
   * identity phase's always.
   */
  configuration: 'interactive' | 'animated' | 'identity';
  /** The axis it follows; the scroll view's own when absent. */
  axis?: 'horizontal' | 'vertical';
  identity: ScrollTransitionEffect;
  /** Going out past the top or leading edge. */
  topLeading: ScrollTransitionEffect;
  /** Going out past the bottom or trailing edge. */
  bottomTrailing: ScrollTransitionEffect;
}

export interface Style {
  /** `.scrollTransition`: effects drawn as the box scrolls in and out of view (scrollTransition.ts). */
  scrollTransition?: ScrollTransition;
}

export interface Style {
  /** `.blendMode`: the CSS `mix-blend-mode` the box is composited with. */
  blendMode?: string;
  /** `.zIndex(_:)`: the box's CSS `z-index` among its siblings. */
  zIndex?: number;
  /**
   * `.alignmentGuide(_:computeValue:)`: the box's guide `name` on `axis` is
   * `a + b·width + c·height` (`value: [a, b, c]`), which the containers that
   * align it by that guide use instead of the default (layout/engine.ts).
   */
  alignmentGuide?: { axis: 'horizontal' | 'vertical'; name: string; value: [number, number, number] };
}

export type DragPhase = 'began' | 'changed' | 'ended' | 'cancelled';

/**
 * A `gesture` with `drag`: coalesced to one per animation frame while
 * `changed`. Coordinates are points relative to the element's box (the
 * root's with `coordinateSpace: "global"`); `velocity` is in points per
 * second (`ended` only, else 0).
 */
export interface DragEvent {
  type: 'drag';
  id: ElementId;
  phase: DragPhase;
  translation: { width: number; height: number };
  location: Point;
  startLocation: Point;
  velocity: { width: number; height: number };
}
/**
 * A `gesture` with `magnify` or `rotate`: two fingers (or a trackpad pinch)
 * since `began`, coalesced to one per animation frame while `changed`.
 * `scale` is the distance between the fingers over the first distance;
 * `rotation` the turn of the line between them, in degrees, clockwise
 * positive; the velocities are per second. `startLocation` is where the
 * fingers' midpoint began, in points from the element's top leading corner;
 * `startAnchor` the same as a fraction of the element's size. `location` is
 * where the midpoint is now (a SwiftBrowser addition: SwiftUI's values stop
 * at the start), so two fingers can pan as they zoom; a trackpad pinch keeps
 * its start. A renderer that cannot tell leaves it out.
 */
export interface PinchEvent {
  type: 'pinch';
  id: ElementId;
  phase: DragPhase;
  scale: number;
  rotation: number;
  scaleVelocity: number;
  rotationVelocity: number;
  startLocation: Point;
  startAnchor: Point;
  location?: Point;
}
/** A `gesture` with `longPress` was held `minimumDuration` without moving 10 px. */
export interface LongPressEvent {
  type: 'longpress';
  id: ElementId;
}
/** One pointer on a `touches` element: a stable `id` while it is down, and its point in the element's coordinates. */
export interface TouchPoint {
  id: number;
  x: number;
  y: number;
}
/**
 * A `touches` element's pointers changed: `began` when one came down,
 * `changed` when they moved (coalesced to one per animation frame), `ended`
 * when one lifted, `cancelled` when the renderer lost them all. `touches` is
 * every pointer still down after the change, so an app reads the state from
 * each event (the keys under the touches) instead of tracking ups and downs.
 */
export interface TouchesEvent {
  type: 'touches';
  id: ElementId;
  phase: DragPhase;
  touches: TouchPoint[];
}
/** A `listrow` was swiped to delete or its edit-mode Delete button tapped. */
export interface DeleteEvent {
  type: 'delete';
  id: ElementId;
}
/** A `list` / `scrollview` with `refreshable` was pulled past 60 pt. */
export interface RefreshEvent {
  type: 'refresh';
  id: ElementId;
}
/**
 * Sent to a scroll target (`scrollID`) inside a `scrollview` with
 * `reportsPosition`: scrolling settled with this target at the view's
 * anchor point. Sent once per change of target.
 */
export interface ScrollPositionEvent {
  type: 'scrollposition';
  id: ElementId;
}
/**
 * Sent to a `styled` box with `scrollVisibility`: the share of it inside its
 * scroll view's visible area came to the threshold or above (`visible`), or
 * fell below it. The first check after the box appears says where it is.
 */
export interface ScrollVisibilityEvent {
  type: 'scrollvisibility';
  id: ElementId;
  visible: boolean;
}
/**
 * A movable `listrow` was dragged to a new place: `to` is the index among
 * its container's children (the `list`'s, or its `section`'s) before which
 * it goes, counted before it leaves (0 ... the child count), as
 * `move(fromOffsets:toOffset:)` takes its destination.
 */
export interface MoveEvent {
  type: 'move';
  id: ElementId;
  to: number;
}
/** A `draggable` was lifted: its payload is made now and kept until `dragend`. */
export interface DragStartEvent {
  type: 'dragstart';
  id: ElementId;
}
/** The lifted `draggable` was dropped or let go. */
export interface DragEndEvent {
  type: 'dragend';
  id: ElementId;
}
/** One thing dragged in from outside the page: text (or a link, `text/uri-list`), or a file's bytes in base64. */
export interface DropItem {
  type: string;
  text?: string;
  name?: string;
  /** A file's bytes as base64 (a Swift app's destination; `file` for a React one). */
  data?: string;
  /**
   * The file as the browser holds it, by reference (a React host asks for
   * `reference` drops, src/dragDrop.ts): never serialized, so a dropped
   * folder of large files costs no memory until a file is read.
   */
  file?: File;
  /** The file's path inside a dropped folder, the folder's name first (`Photos/2025/a.jpg`); absent for a file dropped on its own. */
  path?: string;
  /** Natively: a dropped file still on disk, its `file:` URL and size, read on demand through the files bridge. */
  url?: string;
  size?: number;
}
/**
 * A drag over a `dropdestination`: it `entered`, `exited`, or `performed`
 * (was released over it). `source` is the lifted `draggable`'s id for a drag
 * that started in the app; `items` what came from outside the page. The
 * point is in the destination's own coordinates (0, 0 while entering and leaving).
 */
export interface DropEvent {
  type: 'drop';
  id: ElementId;
  phase: 'entered' | 'exited' | 'performed';
  source?: ElementId;
  items?: DropItem[];
  x: number;
  y: number;
}

/** The Phase 8 events (merged into `RendererEvent` above). */
export type InteractionEvent = DragEvent | PinchEvent | LongPressEvent | TouchesEvent | DeleteEvent | RefreshEvent | ScrollPositionEvent | ScrollVisibilityEvent | MoveEvent | DragStartEvent | DragEndEvent | DropEvent;
// Phase 8: alerts, controls, effects
// (docs/ops-protocol.md, "Phase 8: interaction"; gestures, swipe actions,
// context menus, list rows and refresh live in their own block)
// ---------------------------------------------------------------------------

export type AlertStyle = 'alert' | 'dialog';
/**
 * A root-level element (child of id 0) placed after the tree, like `sheet`.
 * The children are the `button`s in order; a `cancel` role is drawn apart.
 * With no button children the renderer draws an "OK" that sends `tap` with
 * the alert's own id; so does a tap on a dialog's scrim. `textfield`
 * children (`.alert { TextField(...); Button(...) }`) are drawn in the card
 * between the message and the buttons, in their order.
 */
export interface AlertProps {
  title: string;
  message: string | null;
  style: AlertStyle;
}

/** The label (may be empty) is the child; a tap on a half sends `step` ±1. */
export interface StepperProps {
  canIncrement: boolean;
  canDecrement: boolean;
}

export type SliderLabel = 'label' | 'min' | 'max';
/**
 * The children are `[label?, minimumValueLabel?, maximumValueLabel?]`, those
 * present in that order, named by `labels`. Dragging sends `slide` with the
 * value in `min…max` snapped to `step`; `value` is authoritative.
 */
export interface SliderProps {
  value: number;
  min: number;
  max: number;
  step: number | null;
  labels?: SliderLabel[];
}

export type DatePickerComponents = 'date' | 'hourAndMinute' | 'dateAndTime';
/**
 * Compact style: the label child leads, one or two capsules trail showing
 * `seconds` (since 1970, UTC) formatted in US English. A change in the native
 * picker sends `date` with the combined seconds.
 */
export interface DatePickerProps {
  seconds: number;
  components: DatePickerComponents;
  min: number | null;
  max: number | null;
  /** The IANA time zone the date and time are shown and edited in (`\\.timeZone`); the viewer's when absent. */
  timeZone?: string;
  /** `.labelsHidden()`: the label child is not laid out. */
  labelsHidden?: boolean;
}

/** A `<textarea>` filling the proposal; input sends `text` like a `textfield`. */
export interface TextEditorProps {
  text: string;
  /** Bound focus (UIKit's `becomeFirstResponder`): the editor takes or gives up focus (scrolled into view as it does); `focus` events report changes. */
  focused?: boolean;
  /** As a `textfield`'s: Backspace at the start of the text sends `deletebackward`. */
  deleteBackward?: boolean;
  /**
   * Attributed text (`TextEditor(text: $attributedString)`): the runs `text`
   * is made of, drawn with each run's weight, slant, lines and color. An
   * editor with runs (an empty array included) is the rich editor for its
   * life; it still reports plain `text`, and the app keeps the runs.
   */
  runs?: TextRun[];
  /**
   * A bound selection (`selection:`), on a plain editor or a rich one: the
   * editor reports the viewer's with `selection` events, and moves to one
   * the app sets (when the app focuses the editor, if it is not focused).
   */
  selection?: TextSelectionRange;
}

/** A range of an editor's text in UTF-16 offsets; `start === end` is the caret. */
export interface TextSelectionRange {
  start: number;
  end: number;
}

/** A `texteditor` with `selection` in its props: the viewer moved the caret or selected. */
export interface SelectionEvent {
  type: 'selection';
  id: ElementId;
  value: TextSelectionRange;
}

/**
 * A rich `texteditor`'s runs after an edit of their attributes alone (the
 * native editor's own Bold, Italic and Underline, iOS 26's edit menu): the
 * text unchanged, `value` its runs as the editor now shows them, each run's
 * other attributes (a color, a link) as the app sent them.
 */
export interface RunsEvent {
  type: 'runs';
  id: ElementId;
  value: TextRun[];
}

/** A `stepper` half was tapped: +1 or −1. */
export interface StepEvent {
  type: 'step';
  id: ElementId;
  value: number;
}
/** A `slider` was dragged or tapped: the value in its range, snapped to `step`. */
export interface SlideEvent {
  type: 'slide';
  id: ElementId;
  value: number;
  /** `onEditingChanged`: true with the first value of a drag, false with its last; absent for the values between. */
  editing?: boolean;
}
/** A `datepicker` changed: seconds since 1970 (an instant; the zone only shaped the reading). */
export interface DateEvent {
  type: 'date';
  id: ElementId;
  value: number;
}
export type Phase8ControlEvent = StepEvent | SlideEvent | DateEvent | FocusEvent | SubmitEvent | SelectionEvent | RunsEvent | DeleteBackwardEvent;

/**
 * Backspace in a `textfield` or `texteditor` with `deleteBackward`, with
 * nothing selected and the caret at offset 0 (so nothing to delete): the
 * app merges the field into what comes before it, say. The key deletes
 * nothing of its own.
 */
export interface DeleteBackwardEvent {
  type: 'deletebackward';
  id: ElementId;
}

/** Phase 12: a bound `textfield` (one with `focused` in its props) gained or lost focus. */
export interface FocusEvent {
  type: 'focus';
  id: ElementId;
  value: boolean;
}

/** Phase 12: the Enter key in a `textfield` with `submit`. */
export interface SubmitEvent {
  type: 'submit';
  id: ElementId;
}

/** Phase 12: `.preferredColorScheme` arrives as an `update` of the root with this; `null` returns to the viewer's setting. */
export interface RootProps {
  colorScheme?: 'light' | 'dark' | null;
}

export interface ElementPropsByKind {
  alert: AlertProps;
  stepper: StepperProps;
  slider: SliderProps;
  datepicker: DatePickerProps;
  texteditor: TextEditorProps;
}

export const PHASE8_CONTROL_KINDS: readonly ElementKind[] = ['alert', 'stepper', 'slider', 'datepicker', 'texteditor'];

/** `.rotation3DEffect(_:axis:anchor:perspective:)`. */
export interface Rotation3D {
  /** Degrees. */
  angle: number;
  axis: { x: number; y: number; z: number };
  /** Unit point the rotation turns around (default 0.5/0.5). */
  anchor?: UnitPoint;
  /** SwiftUI's perspective factor (default 1 → a 500px CSS perspective). */
  perspective?: number;
}

export interface Style {
  /**
   * CSS `perspective(500px / perspective) rotate3d(x, y, z, angle)`, composed
   * after the 2D rotation and before the scale (see styles.ts `effectsCSS`).
   */
  rotation3D?: Rotation3D;
  /** `.blur(radius:)`: `filter: blur(<radius>px)`, combined with `shadow` in one `filter`. */
  blur?: number;
  /** `.brightness(_:)`: SwiftUI's amount (-1…1), drawn as CSS `brightness(1 + amount)`. */
  brightness?: number;
  /** `.saturation(_:)`: CSS `saturate()`. */
  saturation?: number;
  /** `.contrast(_:)`: CSS `contrast()`. */
  contrast?: number;
  /** `.grayscale(_:)`: CSS `grayscale()` (0…1). */
  grayscale?: number;
  /** `.hueRotation(_:)`: degrees, CSS `hue-rotate()`. */
  hueRotation?: number;
  /** `.colorInvert()`: CSS `invert(1)`. */
  colorInvert?: boolean;
  /**
   * `.colorMultiply(_:)`: each component (and the alpha) multiplied by the
   * color's, drawn with an SVG `feColorMatrix` filter (colorMultiply.ts).
   */
  colorMultiply?: Color;
  /** View `.underline(_:)`: underlines the texts below (false turns an ancestor's off). */
  underline?: boolean;
  /** View `.strikethrough(_:)`: strikes the texts below through (false turns it off). */
  strikethrough?: boolean;
  /** View `.italic(_:)`: slants the texts below (false turns it off). */
  italic?: boolean;
  /**
   * `.scrollDisabled(_:)`, inherited: the views below do not scroll; a
   * `texteditor` is then as tall as its lines (a text view that does not
   * scroll), whatever the proposal. False turns an ancestor's off.
   */
  scrollDisabled?: boolean;
  /**
   * `.environment(\.defaultMinListRowHeight, _)`: the lists below space
   * their rows at least this tall, unless a list sets its own
   * `minRowHeight` (inherited).
   */
  defaultMinListRowHeight?: number;
  /**
   * `.ignoresSafeArea(edges:)`: the engine extends the box to the screen
   * edge on each listed side when the box touches that safe-area edge. The
   * child's proposal and position are unchanged (the extension is background).
   */
  ignoresSafeArea?: 'all' | Edge[];
  /**
   * `.ignoresSafeArea(.keyboard)` (and `.all`, the default): while the
   * on-screen keyboard is up (device mode), the engine extends the box down
   * under it instead of stopping above it, and a root that carries it keeps
   * the keyboardless height.
   */
  ignoresKeyboard?: boolean;
}

/** `.repeatCount(_:autoreverses:)` / `.repeatForever(autoreverses:)`. */
export interface AnimationRepeat {
  count: number | 'forever';
  autoreverses: boolean;
}

export interface Animation {
  /** WAAPI `iterations` (Infinity for `forever`) and `direction: alternate` when autoreversing. */
  repeat?: AnimationRepeat;
}

/** `active` when visible and focused, `inactive` when visible but unfocused, `background` when hidden. */
export type ScenePhase = 'active' | 'inactive' | 'background';

export interface EnvironmentEvent {
  scenePhase?: ScenePhase;
}

// ---------------------------------------------------------------------------
// Multi-line text fields, view section headers, scrolling to a view
// ---------------------------------------------------------------------------

export interface TextFieldProps {
  /**
   * `TextField(_:text:axis: .vertical)`: a `<textarea>` that grows with its
   * text between the enclosing `lineLimitMin` (1) and `lineLimit` (none)
   * lines and scrolls past that; Return breaks the line unless the field has
   * `submit` (then Shift-Return does). Absent for a one-line field.
   */
  axis?: 'vertical';
}

export interface ListProps {
  /** `.scrollContentBackground(.hidden)`: the list draws no background of its own (rows keep theirs). */
  background?: 'hidden';
  /**
   * `.scrollDismissesKeyboard(_:)`: `true` or `immediately` dismisses the
   * keyboard as scrolling starts, `interactively` lets it follow the finger
   * down (natively; a page blurs the text input being edited inside either way).
   */
  dismissesKeyboard?: boolean | 'immediately' | 'interactively';
  /**
   * `.scrollEdgeEffectStyle(_:for:)` / `.scrollEdgeEffectHidden(_:for:)`: the
   * scroll edge effect where the list runs under its screen's bar (`top`) or
   * a bar covering the bottom (`bottom`); an absent edge is automatic (soft).
   */
  edgeEffect?: EdgeEffectProps;
}

export interface EdgeEffectProps {
  top?: 'hard' | 'soft' | 'hidden';
  bottom?: 'hard' | 'soft' | 'hidden';
}

export interface ScrollViewProps {
  /** As for `list`. */
  dismissesKeyboard?: boolean | 'immediately' | 'interactively';
  /** As for `list`. */
  edgeEffect?: EdgeEffectProps;
  /**
   * `.defaultScrollAnchor(_:)`: the content starts with this point of it at
   * the same point of the viewport (`{x: 0.5, y: 1}`: the end in view), and
   * keeps it when the content grows while the viewer is at it (src/scrollAnchor.ts).
   */
  defaultScrollAnchor?: UnitPoint;
}

/**
 * A `Section`'s header or footer that is a view rather than a `Text`: the
 * first (`sectionheader`) or last (`sectionfooter`) child of the `section`,
 * holding the view. In a list it sits above / below the section's card in
 * the content width (footnote font, secondary color); elsewhere it stacks
 * with the rows.
 */
export type SectionSupplementProps = Record<string, never>;

export interface ElementPropsByKind {
  sectionheader: SectionSupplementProps;
  sectionfooter: SectionSupplementProps;
}

/** `ScrollViewProxy.scrollTo(_:anchor:)`: where the target should end up (null: as little scrolling as shows it). */
/**
 * SF Symbols' effects (`.symbolEffect`), drawn with CSS on the styled box:
 * `bounce`, `pulse`, `variableColor` (as a pulse), `scale` (up while
 * active; `down` shrinks), `rotate`, `wiggle`, `breathe`, `appear` /
 * `disappear` (shown / hidden while active). Without `trigger` the effect
 * runs while the style carries it (`repeat` times, or forever); with one, it
 * plays once (`repeat` times) whenever `trigger` changes, like
 * `.symbolEffect(_:value:)`.
 */
export interface SymbolEffectStyle {
  effect: 'bounce' | 'pulse' | 'variableColor' | 'scale' | 'rotate' | 'wiggle' | 'breathe' | 'appear' | 'disappear';
  direction?: 'up' | 'down';
  /** Seconds per run at speed 1 is the effect's own; `speed` divides it. */
  speed?: number;
  repeat?: number | 'forever';
  trigger?: number;
}

export interface Style {
  /** The page sends `hover` events as a mouse enters and leaves the box (`.onHover`). */
  hover?: boolean;
  /**
   * `.onContinuousHover(coordinateSpace:)`: the page sends `continuoushover`
   * events as a mouse moves over the box, its location in the box's own
   * coordinates (`local`) or the root's, scrolling included (`global`, the
   * same a `reportsFrame` geometry event reports), and once as it leaves.
   */
  continuousHover?: 'local' | 'global';
  /** `.help(_:)`: a tooltip on the box. */
  help?: string;
  /** `.pointerStyle(_:)`: the CSS cursor over the box (`pointer`, `text`, `grab`…). */
  pointerStyle?: string;
  symbolEffect?: SymbolEffectStyle;
  /**
   * `.matchedGeometryEffect(id:in:)`: when an animated pass removes a box
   * with this id and inserts another with it, the inserted one starts from
   * the removed one's frame and the removed one's exit travels to the new
   * frame. The namespace is part of the id.
   */
  matchedGeometry?: string;
  /**
   * `.contentTransition(_:)`: how a text under the box changes in a pass that
   * animates. `numericText` rolls the old text out upward and the new one in
   * from below, or the other way when the new text reads as a smaller number
   * (`countsDown` fixes the direction); `opacity` and `interpolate` crossfade
   * them.
   */
  contentTransition?: { kind: 'numericText' | 'opacity' | 'interpolate'; countsDown?: boolean };
}

export interface Style {
  /**
   * `.containerRelativeFrame(_:…)`: on the marked axes the box is
   * `a × container + b` long, the container being the nearest scroll view's
   * visible area (or the screen), and its content is aligned in that frame.
   */
  containerRelativeFrame?: { horizontal?: boolean; vertical?: boolean; a: number; b: number; alignment: FrameAlignment };
}

/**
 * A mouse (or pen) entered (`value: true`) or left a box whose style has
 * `hover`. Touch never hovers.
 */
export interface HoverEvent {
  type: 'hover';
  id: ElementId;
  value: boolean;
}

/**
 * A mouse (or pen) over a box whose style has `continuousHover`: `active`
 * with where it is, coalesced to one per animation frame while it moves,
 * then `ended` once as it leaves. Touch never hovers.
 */
export type ContinuousHoverEvent = { type: 'continuoushover'; id: ElementId; phase: 'active'; location: Point } | { type: 'continuoushover'; id: ElementId; phase: 'ended' };

export interface ScrollToCommand {
  anchor: UnitPoint | null;
  /** Increases with every request (across readers); the page carries out each serial once. */
  serial: number;
  /**
   * The axes to scroll on; both when omitted. `.scrollPosition(id:)` names
   * its scroll view's axes, so setting it scrolls that view alone and not a
   * list the view sits in.
   */
  axes?: ScrollAxes;
}

export interface Style {
  /** `.id(_:)` inside a `ScrollViewReader`: this box is a scroll target. */
  scrollID?: boolean;
  /**
   * Set in the pass in which a `ScrollViewProxy` asks to scroll to this box
   * (and kept until the reader re-runs): after laying out a pass that brings
   * a serial above every one it handled, the page scrolls the scroll views
   * and lists around it (smoothly when the pass's commit is animated).
   */
  scrollTo?: ScrollToCommand;
  /** The lower bound of a `.lineLimit(2...4)` range (absent for 1): a vertical text field's minimum lines. */
  lineLimitMin?: number;
}

// ---------------------------------------------------------------------------
// Video and audio (docs/ops-protocol.md, "Video and audio")
// ---------------------------------------------------------------------------

/**
 * A `video` element: an `AVPlayer` shown by a view (AVKit's `VideoPlayer`, an
 * `AVPlayerLayer`), as an HTML `<video>` (src/video.ts). No children.
 */
/**
 * Lazy rows: the rows of a `List` or lazy stack that are not built yet
 * (`remaining` of them, after the built ones). It takes no space; when it
 * comes within a screen of its scroll view's visible area the page sends a
 * `lazy` event, and Swift builds the next rows (a new spacer follows them).
 */
export interface LazySpacerProps {
  axis: 'vertical' | 'horizontal';
  remaining: number;
}

/** Lazy menus: the viewer opened (`open: true`) or closed a deferred `menu` / `contextmenu`. */
export interface MenuEvent {
  type: 'menu';
  id: ElementId;
  open: boolean;
}

/** A `lazyspacer` came near the visible area: build the next rows. */
export interface LazyEvent {
  type: 'lazy';
  id: ElementId;
}

export interface VideoProps {
  /** The media URL (remote, data or blob); null while there is none. */
  url: string | null;
  /** Whether the player plays here (only the newest view of a player plays it). */
  playing: boolean;
  muted: boolean;
  /** 0…1. */
  volume: number;
  /** The playback rate (positive; the player's default rate while paused). */
  rate: number;
  /** An `AVPlayerLooper` loops the item. */
  loop: boolean;
  /** CSS `object-fit`: `contain`, `cover`, `fill`. */
  gravity: 'resizeAspect' | 'resizeAspectFill' | 'resize';
  /** The browser's native playback controls (`VideoPlayer`, `AVPlayerViewController`). */
  controls: boolean;
  /** Seconds; the page seeks once per serial (and reports `seeked` with it). */
  seek: { time: number; serial: number };
  /** Report the position this often (seconds) while playing, when shorter than `timeupdate`'s pace. */
  report?: number;
  /** The video's size in pixels, once known: the aspect ratio of an axis left open. */
  width?: number;
  height?: number;
}

export interface ElementPropsByKind {
  video: VideoProps;
  lazyspacer: LazySpacerProps;
}

/**
 * A gradient of a `Canvas` (`GraphicsContext.Shading`), in the drawing's own
 * coordinates (points, after the command's `transform`) rather than the
 * unit square of a view's box.
 */
export interface CanvasLinearGradientShading {
  canvasLinearGradient: { stops: GradientStop[]; start: { x: number; y: number }; end: { x: number; y: number } };
}
export interface CanvasRadialGradientShading {
  canvasRadialGradient: { stops: GradientStop[]; center: { x: number; y: number }; startRadius: number; endRadius: number };
}
export type CanvasShading = ShapeStyle | CanvasLinearGradientShading | CanvasRadialGradientShading;

/** A `GraphicsContext.Filter`: one key, its amount (degrees for `hueRotation`). */
export type CanvasFilter =
  | { blur: number }
  | { shadow: { color: Color; radius: number; x: number; y: number } }
  | { grayscale: number }
  | { saturation: number }
  | { brightness: number }
  | { contrast: number }
  | { hueRotation: number }
  | { invert: number };

/** The context's state when a command was drawn. */
export interface CanvasState {
  /** `[a, b, c, d, tx, ty]`, as `CGAffineTransform` (and SVG's `matrix()`); absent for the identity. */
  transform?: [number, number, number, number, number, number];
  opacity?: number;
  /** CSS `mix-blend-mode`. */
  blendMode?: string;
  /** SVG path data, in the canvas's coordinates; the command draws inside all of them. */
  clips?: string[];
  filters?: CanvasFilter[];
}

export type CanvasCommand = CanvasState &
  (
    | { op: 'fill'; path: string; shading: CanvasShading; evenOdd?: boolean }
    | {
        op: 'stroke';
        path: string;
        shading: CanvasShading;
        lineWidth: number;
        lineCap?: 'butt' | 'round' | 'square';
        lineJoin?: 'miter' | 'round' | 'bevel';
        miterLimit?: number;
        dash?: number[];
        dashPhase?: number;
      }
    | {
        op: 'image';
        image: ImageProps;
        /** The point `anchor` (a unit point of the image) is placed at. */
        x: number;
        y: number;
        anchor: UnitPoint;
        /** `draw(_:in:)`: the rectangle's size; otherwise the image's own size. */
        width?: number;
        height?: number;
        /** A resolved image's `shading`: a template image is painted with it. */
        shading?: CanvasShading;
      }
    | { op: 'text'; text: string; x: number; y: number; anchor: UnitPoint; shading: CanvasShading; font?: Font }
  );

/**
 * A `Canvas` at one size (src/canvasDrawing.ts): the commands its renderer
 * drew for `width` × `height`, the size the canvas reported. It fills its
 * proposal and clips the drawing to its box.
 */
export interface CanvasProps {
  width: number;
  height: number;
  commands: CanvasCommand[];
}

export interface ElementPropsByKind {
  canvas: CanvasProps;
}

/**
 * What a `video` element reports: `ready` (metadata), `time`, `status`
 * (playing, waiting, paused), `seeked` (a `seek` serial done), `play` /
 * `pause` (the viewer used the controls; `reason: "blocked"` when the browser
 * refused to start), `volume`, `rate`, `ended`, `error` (a `MediaError` code).
 */
export interface VideoEvent {
  type: 'video';
  id: ElementId;
  event: 'ready' | 'time' | 'status' | 'seeked' | 'play' | 'pause' | 'volume' | 'rate' | 'ended' | 'error';
  url?: string;
  time?: number;
  duration?: number | null;
  width?: number;
  height?: number;
  status?: 'playing' | 'waiting' | 'paused';
  serial?: number;
  reason?: 'blocked';
  muted?: boolean;
  volume?: number;
  rate?: number;
  code?: number;
  message?: string;
}

/** `media` requests (src/media.ts): AVFoundation's files and probes, QuickLook's previews. */
/** A remote command MediaPlayer's `MPRemoteCommandCenter` handles (docs/ops-protocol.md, "Now playing"). */
export type MediaSessionCommand = 'play' | 'pause' | 'stop' | 'nextTrack' | 'previousTrack' | 'seekTo' | 'skipForward' | 'skipBackward';

/**
 * `mediaSession` (src/mediaSession.ts): MediaPlayer's now-playing info and
 * remote commands over the browser's Media Session API. `update` sets what
 * the session shows and which commands the app handles (a field left out
 * stays as it was); `receive` answers the next command the viewer gave.
 */
export interface MediaSessionRequestParams {
  action: 'update' | 'receive';
  /** `update`: what plays, for the lock screen and the media controls (`artwork` a URL); null clears it. */
  metadata?: { title?: string; artist?: string; album?: string; artwork?: string } | null;
  playbackState?: 'none' | 'playing' | 'paused';
  /** `update`: the position in seconds, which the browser extrapolates at `rate`; null clears it. */
  position?: { duration: number; position: number; rate: number } | null;
  /** `update`: the commands the app handles; the others get no button. */
  commands?: MediaSessionCommand[];
  /** `update`: the app's preferred skip intervals in seconds, answered with a skip the system gives no offset for. */
  skip?: { forward?: number; backward?: number };
}

export interface MediaRequestParams {
  action: 'file' | 'probe' | 'release' | 'open';
  url?: string;
  name?: string;
  type?: string;
  data?: string;
  /** `file`: a resource's path inside the app's bundle, in place of `data`: the page answers the URL it serves the resource at. */
  path?: string;
}

// ---------------------------------------------------------------------------
// Scroll geometry, settable offsets, pinned views, animation completion
// (docs/ops-protocol.md, "Scroll geometry, pinned views and animation completion")
// ---------------------------------------------------------------------------

export interface ScrollViewProps {
  /**
   * `.onScrollGeometryChange(for:of:action:)`: the page sends `scrollgeometry`
   * to the view after each layout pass and, while it scrolls, once per
   * animation frame, whenever a field changed (src/scrollGeometry.ts).
   */
  reportsGeometry?: boolean;
  /**
   * `ScrollPosition.scrollTo(x:y:)` (a `.scrollPosition` bound to a point):
   * the content offset to scroll to, carried out once per `serial` after the
   * pass's layout, smoothly when the commit is animated. An axis left out
   * keeps its offset; the browser clamps the ends.
   */
  scrollOffset?: ScrollOffsetCommand;
}

export interface ScrollOffsetCommand {
  x?: number;
  y?: number;
  /** Increases with every request; the page carries out each serial once. */
  serial: number;
}

/**
 * SwiftUI's `ScrollGeometry` for a `scrollview` with `reportsGeometry`:
 * where its visible area is in its content and how big both are, in points
 * (2 decimals). Sent after a layout pass and, while the view scrolls, once
 * per animation frame, when any field changed since the last report.
 */
export interface ScrollGeometryEvent {
  type: 'scrollgeometry';
  id: ElementId;
  /** The visible area's origin in the content (`scrollLeft`, `scrollTop`). */
  offset: { x: number; y: number };
  /** The content's laid-out size. */
  contentSize: { width: number; height: number };
  /** The scroll view's own size. */
  containerSize: { width: number; height: number };
  /** Space the view scrolls into past its content (under a bar covering its bottom); zeros on the web otherwise. */
  contentInsets: { top: number; leading: number; bottom: number; trailing: number };
}

/** `PinnedScrollableViews`: what a lazy stack or grid keeps in view as its sections scroll. */
export type PinnedView = 'sectionHeaders' | 'sectionFooters';

export interface VStackProps {
  /**
   * `LazyVStack(pinnedViews:)`: the `sectionheader` (`sectionfooter`) of each
   * `section` child stays at the scroll view's edge while its section
   * passes, pushed out by the next section's (src/pinnedViews.ts).
   */
  pinned?: PinnedView[];
}
export interface HStackProps {
  /** `LazyHStack(pinnedViews:)`: as for `vstack`, along the leading edge. */
  pinned?: PinnedView[];
}
export interface LazyVGridProps {
  /** `LazyVGrid(pinnedViews:)`: as for `vstack`. */
  pinned?: PinnedView[];
}
export interface LazyHGridProps {
  /** `LazyHGrid(pinnedViews:)`: as for `hstack`. */
  pinned?: PinnedView[];
}

export interface CommitOp {
  /**
   * `withAnimation(_:completionCriteria:_:completion:)`: the page answers
   * `animationcomplete` with this serial once the pass's animations have
   * ended (at once, after the pass, when nothing animated).
   */
  completion?: number;
  /** `logicallyComplete` (the default) ends with the animation's duration; `removed` once every effect is gone. */
  completionCriteria?: 'logicallyComplete' | 'removed';
}

/** The pass whose `commit` carried `completion` has finished animating. Not addressed to an element. */
export interface AnimationCompleteEvent {
  type: 'animationcomplete';
  serial: number;
}

/**
 * The system's undo or redo (⌘Z, ⇧⌘Z outside a text input; src/undo.ts),
 * for the app's `UndoManager`, which takes a step back or forward. Not
 * addressed to an element; sent once the app has reported an `undo` request.
 */
export interface UndoEvent {
  type: 'undo' | 'redo';
}
